import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    Collapse,
    Dialog,
    IconButton,
    List,
    ListItem,
    Typography,
    ButtonBase,
    TextField,
    Tooltip,
    Switch,
    FormControlLabel,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import {ArrowBack, ArrowDropDown, ArrowDropUp, Close, PeopleOutline, MenuBook, Schedule} from "@material-ui/icons";
import RemoveIcon from "@material-ui/icons/Remove";
import useStyles from '../../../assets/styles/ProgramOutlineStyles';
import {useHistory, useLocation, useParams} from "react-router-dom";
import {DQC_COLORS, KSAB_COLOR_MAPPING, LOCAL_STORAGE_KEYS, REQUEST_TYPE, USER_ACCESS_LEVEL} from "../../../utils/constants";
import {useAuth} from "../../../context/auth";
import {
    generateLearningProgramContentService,
    getInsiderProgramService,
    getLearningProgramPreviewService,
    listV3Level7DQCodeService,
    updateLearningProgramService,
    getActiveChatInsiderProgramService,
    clearChatStateInsiderService,
    removeModuleFromProgramService,
    removeUnitFromModuleService,
    removeSessionFromUnitService,
    getProgramSelectedFrameworksService,
    getUnrestrictedLearningProgramPreviewService,
} from "../../../services/learning_programs.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {toastWarning} from "../../../utils/utils";
import EditIcon from "@material-ui/icons/Edit";
import OutlinePdfDoc from "./components/shared/OutlinePdfDoc.comp";
import OutlineHtmlDoc from "./components/shared/OutlineHtmlDoc.comp";
import PrintIcon from "@material-ui/icons/Print";
import SaveIcon from "@material-ui/icons/Save";
import { pdf } from '@react-pdf/renderer';
import { useReactToPrint } from 'react-to-print';
import { styled } from '@mui/material/styles';
import { AVAILABLE_LANGUAGES } from "../../../assets/styles/SlideEditStyles";
import CombinedEditableSection from "../components/CombinedEditableSection.comp";
import { isUiRtl, normalizeUiLocale, tUi } from "../../../utils/dq_lc";
import {getProgramPreviewPath} from "./DQProProgPreviewRedirect.page";

const TARGET_GROUP_KEYS = {
    General: "outline.targetGroup.general",
    Children: "outline.targetGroup.children",
    Teens: "outline.targetGroup.teens",
};

const AGE_TO_TIER_MAP = {
    Below5: 'T1',
    '5-7YO': 'T1',
    '8-9YO': 'T1',
    '10-12YO': 'T2',
    '13-14YO': 'T3',
    '15-18YO': 'T4',
    '19-24YO': 'T5',
    '25-34YO': 'T7',
    '35-44YO': 'T7',
    '45-54YO': 'T7',
    '55-64YO': 'T7',
    Above65: 'T6',
    All: 'T6',
    Workforce: 'T7',
};

const TARGET_TIER_KEYS = {
    T1: 'outline.targetTier.T1',
    T2: 'outline.targetTier.T2',
    T3: 'outline.targetTier.T3',
    T4: 'outline.targetTier.T4',
    T5: 'outline.targetTier.T5',
    T6: 'outline.targetTier.T6',
    T7: 'outline.targetTier.T7',
};

const TARGET_AGE_KEYS = {
    "8-9YO": "outline.targetAge.8_9",
    "10-12YO": "outline.targetAge.10_12",
    "13-14YO": "outline.targetAge.13_14",
    "15-18YO": "outline.targetAge.15_18",
    // Audience-type keys the ML engine records for general-public and workforce programs
    "All": "outline.targetAge.allAges",
    "Workforce": "outline.targetAge.workforce",
};

// ============= STYLED COMPONENTS FOR EDIT MODE =============

const EditModeToolbar = styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: 0,
    zIndex: 1200,
    width: 'calc(100% + 128px)',
    height: '72px',
    backgroundColor: '#EE6D29',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 40px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    animation: 'fadeIn 0.3s ease-out',
    '@keyframes fadeIn': {
        from: {
            opacity: 0,
            transform: 'translateY(-10px)',
        },
        to: {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
}));

const getProgramLayoutPreviewFromResponse = (resData) => {
    const previews = resData?.program_layout_previews || {};
    const languages = Object.keys(previews);
    const otherLanguage = languages.find(lang => lang !== 'en-US');
    
    // Both languages now have the same structure - direct object with modules
    const englishProgram = previews['en-US']?.program_layout_preview;
    const otherProgram = otherLanguage ? previews[otherLanguage]?.program_layout_preview : null;
    
    // Default to the non-English language if it exists (even if modules are empty)
    const hasOtherLanguage = Boolean(otherLanguage);
    const language = hasOtherLanguage ? otherLanguage : 'en-US';
    const program = hasOtherLanguage ? otherProgram : englishProgram;

    return {
        language,
        languages,
        program,
        englishProgram,
        isEditableLanguage: hasOtherLanguage,
    };
};


const DQProProgOutline = ({ embedded = false, programCode: programCodeProp, isPublicPreview = false, initialDqcoList = [] }) => {
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:600px)');
    const { authUser } = useAuth();
    // Level 22 (partial-access, single-institute) users only preview an
    // already-built program — editing isn't available to them here.
    const isLevel22 = authUser?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    const { code: paramCode } = useParams();
    const dq_module_code = programCodeProp || paramCode;
    const location = useLocation();
    const history = useHistory();
    const [outlineStatus, setOutlineStatus] = useState(
        location.state?.outlineStatus || {
            is_outline_ready: null,
            generation_initiated: null
        }
    );
    const [programData, setProgramData] = useState(location.state?.assessment || null);
    const [programDuration, setProgramDuration] = useState( 0);
    const [programAudience, setProgramAudience] = useState('');
    const [modules, setModules] = useState([]); // Current language modules (displayed by default)
    const [englishModules, setEnglishModules] = useState([]); // Always store English version
    const [englishProgramLayout, setEnglishProgramLayout] = useState(null); // Full English layout object
    const [currentProgram, setCurrentProgram] = useState(null); // Store full program object to preserve all fields
    const [loading, setLoading] = useState(true);
    const [currentLanguage, setCurrentLanguage] = useState('en-US');
    const [isEditableLanguage, setIsEditableLanguage] = useState(false);
    
    // EDIT MODE STATE
    // Filter out English - editing only allowed for other languages
    const editableLanguages = AVAILABLE_LANGUAGES.filter(lang => lang.code !== 'en');
    const defaultEditLanguage = editableLanguages.length > 0 ? editableLanguages[0].code : 'es';
    const [isEditMode, setIsEditMode] = useState(false);
    const [outlineEdits, setOutlineEdits] = useState({}); // { 'module_code_field': { original, edited, language }, 'unit_code_field': {...}, 'session_code_field': {...} }
    const [currentEditLanguage] = useState(defaultEditLanguage);
    const [availableEditLanguages] = useState(editableLanguages);
    const [showEnglishContent, setShowEnglishContent] = useState(false); // Toggle to show English (original) content vs edited content
    const translationKeys = Object.keys(programData?.translations || {});
    const nonEnglishTranslationKey = translationKeys.find(lang => lang && lang !== 'en-US');
    const translationLanguageKey = (currentLanguage !== 'en-US' && programData?.translations?.[currentLanguage]) ? currentLanguage : nonEnglishTranslationKey;
    const isEnglishOnlyProgram = !translationLanguageKey || translationLanguageKey === 'en-US';
    // Get base translated name and description from translations
    const translatedName = (programData?.translations?.[translationLanguageKey]?.name || programData?.translations?.[translationLanguageKey]?.full_name || '');
    const translatedDescription = (programData?.translations?.[translationLanguageKey]?.description || '');

    const baseOtherLangProgramName = !isEnglishOnlyProgram && String(translatedName).trim() ? translatedName : (programData?.full_name || '');
    const baseOtherLangProgramDescription = !isEnglishOnlyProgram && String(translatedDescription).trim() ? translatedDescription : (programData?.description || '');

    // Apply edits if available (only for non-English languages)
    const otherLangProgramName = !isEnglishOnlyProgram && outlineEdits['program_name']?.edited 
        ? outlineEdits['program_name'].edited 
        : baseOtherLangProgramName;
    const otherLangProgramDescription = !isEnglishOnlyProgram && outlineEdits['program_overview']?.edited 
        ? outlineEdits['program_overview'].edited 
        : baseOtherLangProgramDescription;
    
    // Get current language name
    const currentLanguageName = AVAILABLE_LANGUAGES.find(lang => lang.code === currentLanguage)?.name || currentLanguage;

    // UI locale: prefer program's available language from the start so labels don't flip when module data loads.
    // Before layout fetch, currentLanguage is 'en-US'; use nonEnglishTranslationKey so UI is already in the right language.
    const effectiveContentLanguage =
        currentLanguage !== 'en-US' ? currentLanguage : (nonEnglishTranslationKey || currentLanguage);
    const uiLocale = showEnglishContent ? 'en-US' : normalizeUiLocale(effectiveContentLanguage);
    const isRtl = isUiRtl(uiLocale);

    const getTargetGroupLabel = (groupKey) => {
        const k = TARGET_GROUP_KEYS[groupKey];
        return k ? tUi(k, uiLocale) : (groupKey || '');
    };

    const getTargetAgeLabel = (ageKey) => {
        // 1. Direct tier check from programData (or if ageKey is already a tier T1-T7)
        const directTier = programData?.program_details?.Tier
            || programData?.program_details?.wizard_details?.Tier
            || (typeof ageKey === 'string' && /^T[1-7]$/.test(ageKey.trim()) ? ageKey.trim() : null);

        if (directTier && TARGET_TIER_KEYS[directTier]) {
            return tUi(TARGET_TIER_KEYS[directTier], uiLocale);
        }

        // 2. Map raw age band to tier
        const normalizedAge = typeof ageKey === 'string' ? ageKey.trim() : '';
        const mappedTier = AGE_TO_TIER_MAP[normalizedAge];
        if (mappedTier && TARGET_TIER_KEYS[mappedTier]) {
            return tUi(TARGET_TIER_KEYS[mappedTier], uiLocale);
        }

        // 3. Fallback to legacy age mapping or raw key
        const k = TARGET_AGE_KEYS[ageKey];
        return k ? tUi(k, uiLocale) : (ageKey || tUi('outline.targetAge.allAges', uiLocale));
    };

    const getKsabLabel = (key) => {
        const normalized = String(key || '').toLowerCase();
        const map = {
            knowledge: 'outline.ksab.knowledge',
            skills: 'outline.ksab.skills',
            attitudes: 'outline.ksab.attitudes',
            behaviours: 'outline.ksab.behaviours',
        };
        const k = map[normalized];
        return k ? tUi(k, uiLocale) : (key || '');
    };

    const [expandedUnits, setExpandedUnits] = useState([]);
    const [expandedModules, setExpandedModules] = useState([]);
    const [expandedSessions, setExpandedSessions] = useState({});
    const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [itemToRemove, setItemToRemove] = useState({ type: null, m_id: null, u_id: null, s_id: null });
    const [popupContent, setPopupContent] = useState(null);
    // Code currently being fetched → drives the in-chip loader (first click can
    // take 5-10s while the DQ-code list loads; button spinner, not full page).
    const [dqcoLoadingCode, setDqcoLoadingCode] = useState(null);
    // Frameworks actually selected/confirmed for this program at build time
    // (fetched once on load; used by both the "Frameworks Aligned" popup and
    // the printable outline doc, so both agree with what the user picked).
    const [selectedFrameworksList, setSelectedFrameworksList] = useState([]);
    const [isEducatorView, setIsEducatorView] = useState(false);
    const [dqcoList, setDqcoList] = useState(
        initialDqcoList && initialDqcoList.length > 0
            ? initialDqcoList
            : (location.state?.dqcoList || [])
    );

    useEffect(() => {
        if (initialDqcoList && initialDqcoList.length > 0 && dqcoList.length === 0) {
            setDqcoList(initialDqcoList);
        }
    }, [initialDqcoList, dqcoList.length]);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');
    const [isEditingProgramName, setIsEditingProgramName] = useState(false);
    const [openStartOverDialog, setOpenStartOverDialog] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [openLastItemDialog, setOpenLastItemDialog] = useState(false);
    const [lastItemToRemove, setLastItemToRemove] = useState({ type: null, m_id: null, u_id: null, s_id: null });
    const [willDeleteParent, setWillDeleteParent] = useState(null); // 'program', 'module', or 'unit'
    const [openNewProgramDialog, setOpenNewProgramDialog] = useState(false);
    const [frameAlignedContent, setFrameAlignedContent] = useState();
    const [openGenerateConfirmDialog, setOpenGenerateConfirmDialog] = useState(false);
    const [openExitEditConfirmDialog, setOpenExitEditConfirmDialog] = useState(false);
    const [pendingNavigateTo, setPendingNavigateTo] = useState(null);

    // Ref for HTML-based printing (supports ALL languages!)
    const outlineHtmlRef = useRef();

    // React-to-print handler for multi-language Outline PDF
    const handlePrintOutline = useReactToPrint({
        content: () => outlineHtmlRef.current,
        documentTitle: `${programData?.full_name || 'Program'} - Outline`,
        onBeforeGetContent: () => {
            console.log('Preparing to print Outline...');
        },
        onAfterPrint: () => {
            console.log('Outline printed successfully');
        },
        onPrintError: (error) => {
            console.error('Print error:', error);
            toastWarning(tUi('outline.toast.failedOpenPdf', uiLocale));
        },
    });

    const hasUnsavedOutlineEdits = Object.keys(outlineEdits).length > 0;

    useEffect(() => {
        const fetchProgramData = async () => {
            try {
                // Fetch if we don't have programData or if it's missing required fields
                if (dq_module_code) {
                    if (isPublicPreview) {
                        return; // Handled by fetchProgramLayout with unrestricted preview
                    }
                    const data = !programData || !programData.full_name || !programData.description;
                    if (data) {
                        setLoading(true);
                        const response = await getInsiderProgramService(dq_module_code);
                        if (response?.data?.learning_program) {
                            const program = response.data.learning_program;
                            setProgramData(program);
                            console.log('Fetched program data:', program);
                            console.log('Program name:', program.full_name);
                            console.log('Program description:', program.description);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching program data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramData().then(r => console.log(r));
    }, [programData, dq_module_code]);

    // Helper function to convert API module structure to display structure
    // API shape: modules[].module_title, units[].sessions[]
    const mapModulesToDisplay = (apiModules) => {
        return (apiModules || []).map(mod => ({
            module_code: mod.module_code || '',
            module_name: mod.module_title || '',
            module_educator_description: mod.description_for_educator || '',
            module_learner_description: mod.description_for_learner || '',
            module_duration: mod.total_duration_minutes || '',
            module_items_definitions: mod.module_items_definitions || {
                title: '',
                description: ''
            },
            unique_dqco: mod.unique_dqco || [],
            unique_frame_arc: mod.unique_frame_arc || [],
            units: (mod.units || []).map(unit => ({
                unit_code: unit.unit_code || '',
                unit_title: unit.unit_title || '',
                unit_educator_description: unit.description_for_educator || '',
                unit_learner_description: unit.description_for_learner || '',
                unit_duration: unit.total_duration_minutes || 0,
                lessons: (unit.sessions || []).map(session => ({
                    lesson_title: session.session_title || '',
                    lesson_duration: session.duration_minutes || 0,
                    lesson_educator_description: session.description_for_educator || '',
                    lesson_learner_description: session.description_for_learner || '',
                    session_learning_objectives: session.session_learning_objectives || [],
                    session_format: session.session_format || '',
                    session_unique_ksab: session.unique_ksab || [],
                    session_code: session.session_code || '',
                })),
            })),
        }));
    };

    useEffect(() => {
        const fetchModules = async () => {
            if (!dq_module_code) {
                setModules([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // Fetch program first so we can prefer saved outline over preview (preview can be stale)
                const programResponse = isPublicPreview
                    ? null
                    : await getInsiderProgramService(dq_module_code);
                const latestProgramData = programResponse?.data?.learning_program || null;
                if (latestProgramData) setProgramData(latestProgramData);

                const res = await (isPublicPreview
                    ? getUnrestrictedLearningProgramPreviewService(dq_module_code)
                    : getLearningProgramPreviewService(dq_module_code));
                if (isPublicPreview && res?.data) {
                    setProgramData(res.data);
                }
                if (!res?.data?.is_outline_ready && !isPublicPreview) {
                    toastWarning('Learning program outline not ready yet!');
                    setLoading(false);
                    return;
                }
               const { program, language, englishProgram, isEditableLanguage } = getProgramLayoutPreviewFromResponse(res.data, currentLanguage);
                setCurrentLanguage(language);
                setIsEditableLanguage(isEditableLanguage);
                setEnglishProgramLayout(englishProgram);

                // Store English version for reference/comparison
                if (englishProgram?.modules && Array.isArray(englishProgram.modules)) {
                    setEnglishModules(mapModulesToDisplay(englishProgram.modules));
                }
                
                // Prefer saved outline from program API when available; else use preview
                const savedOutline = language === 'en-US'
                    ? latestProgramData?.program_outline_draft
                    : latestProgramData?.session_updates?.[language]?.program_layout_preview;

                const programToUse = (savedOutline?.modules && Array.isArray(savedOutline.modules) && savedOutline.modules.length > 0)
                    ? savedOutline
                    : program;

                if (programToUse?.modules && Array.isArray(programToUse.modules)) {
                    setCurrentProgram(programToUse);
                    setProgramDuration(programToUse.total_duration_minutes);
                    setProgramAudience(programToUse.target_age_group);
                    setModules(mapModulesToDisplay(programToUse.modules));
                } else {
                    setModules([]);
                }
            } catch (err) {
                console.error('Error fetching modules:', err);
                setModules([]);
            } finally {
                setLoading(false);
            }
        };

        fetchModules().then(r => console.log(r));
    }, [dq_module_code]);

    useEffect(() => {
        if (dqcoList.length === 0) {
            fetchV3Level7DQCodeList();
        }
        // eslint-disable-next-line
    }, [uiLocale]);

    // Frameworks actually selected/confirmed for this program at build time —
    // used by the "Frameworks Aligned" popup and the printable outline doc.
    useEffect(() => {
        if (!dq_module_code) return;
        getProgramSelectedFrameworksService(dq_module_code)
            .then(res => setSelectedFrameworksList(res?.data?.frameworks || []))
            .catch(err => {
                console.error('Error fetching selected frameworks:', err);
                setSelectedFrameworksList([]);
            });
    }, [dq_module_code]);

    // Initialize module expansion when modules are loaded
    useEffect(() => {
        if (modules.length > 0 && !hasInitializedExpansion) {
            // Expand all modules by default
            setExpandedModules(modules.map((_, index) => index));
            setHasInitializedExpansion(true);
        }
    }, [modules, hasInitializedExpansion]);

    const handleEditDescription = () => {
        if (isLevel22) return;
        setEditedDescription(programData?.description || '');
        setIsEditingDescription(true);
    };

    const handleCancelEdit = () => {
        setIsEditingDescription(false);
        setEditedDescription('');
    };

    const handleEditProgramName = () => {
        setIsEditingProgramName(true);
    };

    const handleCancelEditProgramName = () => {
        setIsEditingProgramName(false);
    };

    const handleSaveDescription = async () => {
        try {
            setLoading(true);
            const payload = {
                description: editedDescription
            };

            const response = await updateLearningProgramService(dq_module_code, payload);

            if (response && response.data) {
                // Update local state with new description
                setProgramData(prev => ({
                    ...prev,
                    description: editedDescription
                }));

                setIsEditingDescription(false);
                setEditedDescription('');

                // Optional: Show success message
                // toastSuccess('Description updated successfully');
            } else {
                toastWarning('Failed to update description');
            }
        } catch (error) {
            console.error('Error updating description:', error);
            toastWarning('Failed to update description');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProgramName = async () => {
        try {
            setLoading(true);
            const payload = {
                full_name: programData?.full_name
            };

            const response = await updateLearningProgramService(dq_module_code, payload);

            if (response && response.data) {
                // Update local state with new program name
                setProgramData(prev => ({
                    ...prev,
                    full_name: programData?.full_name
                }));

                setIsEditingProgramName(false);
            } else {
                toastWarning('Failed to update program name');
            }
        } catch (error) {
            toastWarning('Failed to update program name');
        } finally {
            setLoading(false);
        }
    };

    const toggleUnit = (unitCode) => {
        setExpandedUnits((prev) =>
            prev.includes(unitCode) ? prev.filter(code => code !== unitCode) : [...prev, unitCode]
        );
    };

    const toggleModule = (moduleIndex) => {
        setExpandedModules((prev) =>
            prev.includes(moduleIndex) ? prev.filter(i => i !== moduleIndex) : [...prev, moduleIndex]
        );
    };

    const toggleSession = (sessionCode) => {
        setExpandedSessions((prev) => ({
            ...prev,
            [sessionCode]: !prev[sessionCode],
        }));
    };

    const fetchV3Level7DQCodeList = async () => {
        try {
            const res = await listV3Level7DQCodeService(uiLocale);
            const list = res?.data?.metaData || [];
            setDqcoList(list);
            return list; // return so callers can use it immediately (state update is async)
        } catch (err) {
            setDqcoList([]);
            return [];
        }
    };

    // Unified function to refetch all program data
    const refetchProgramData = async (options = { skipOutlineUpdate: false }) => {
        if (!dq_module_code) {
            setModules([]);
            return;
        }

        try {
            setLoading(true);

            // Fetch updated program info
            const programResponse = await getInsiderProgramService(dq_module_code);
            const latestProgramData = programResponse?.data?.learning_program || null;
            if (latestProgramData) setProgramData(latestProgramData);

            // Fetch updated modules and program structure
            const res = await getLearningProgramPreviewService(dq_module_code);
            if (!res?.data?.is_outline_ready) {
                toastWarning('Learning program outline not ready yet!');
                return;
            }

            const { program, language, englishProgram, isEditableLanguage } = getProgramLayoutPreviewFromResponse(res.data, currentLanguage);
            setCurrentLanguage(language);
            setIsEditableLanguage(isEditableLanguage);
            setEnglishProgramLayout(englishProgram);

            // Also fetch English version for comparison
            if (englishProgram?.modules && Array.isArray(englishProgram.modules)) {
                setEnglishModules(mapModulesToDisplay(englishProgram.modules));
            }

            // Avoid stale preview overwrites right after a save
            if (options?.skipOutlineUpdate) return;

            // Prefer saved outline from program API when available; else use preview
            const savedOutline = language === 'en-US'
                ? latestProgramData?.program_outline_draft
                : latestProgramData?.session_updates?.[language]?.program_layout_preview;

            const programToUse = (savedOutline?.modules && Array.isArray(savedOutline.modules) && savedOutline.modules.length > 0)
                ? savedOutline
                : program;

            if (programToUse?.modules && Array.isArray(programToUse.modules)) {
                setCurrentProgram(programToUse);
                setProgramDuration(programToUse.total_duration_minutes);
                setProgramAudience(programToUse.target_age_group);
                setModules(mapModulesToDisplay(programToUse.modules));
            } else {
                setModules([]);
            }
        } catch (err) {
            console.error('Error refetching program data:', err);
            setModules([]);
        } finally {
            setLoading(false);
        }
    };

    const getEdit = (key, fallback) => outlineEdits[key]?.edited || fallback;

    const applyEditsToModules = () => {
        if (!currentProgram?.modules) return [];

        return currentProgram.modules.map(mod => ({
            ...mod,
            module_title: getEdit(`${mod.module_code}_module_name`, mod.module_title),
            description_for_educator: getEdit(`${mod.module_code}_educator_description`, mod.description_for_educator),
            description_for_learner: getEdit(`${mod.module_code}_learner_description`, mod.description_for_learner),

            units: (mod.units || []).map(unit => ({
                ...unit,
                unit_title: getEdit(`${unit.unit_code}_unit_title`, unit.unit_title),
                description_for_educator: getEdit(`${unit.unit_code}_educator_description`, unit.description_for_educator),
                description_for_learner: getEdit(`${unit.unit_code}_learner_description`, unit.description_for_learner),

                sessions: (unit.sessions || []).map(session => ({
                    ...session,
                    session_title: getEdit(`${session.session_code}_lesson_title`, session.session_title),
                    description_for_educator: getEdit(`${session.session_code}_educator_description`, session.description_for_educator),
                    description_for_learner: getEdit(`${session.session_code}_learner_description`, session.description_for_learner),

                    session_learning_objectives: (session.session_learning_objectives || []).map(slo => ({
                        ...slo,
                        description_for_educator: getEdit(
                            `${session.session_code}_slo_${slo.slo_code}_description_for_educator`,
                            slo.description_for_educator
                        ),
                        description_for_learner: getEdit(
                            `${session.session_code}_slo_${slo.slo_code}_description_for_learner`,
                            slo.description_for_learner
                        ),
                    })),
                })),
            })),
        }));
    };

    const createProgramOutlineDraft = (modules) => {
        // Ensure currentProgram is an object, not an array or null
        // If currentProgram is an array, that means the API returned array structure
        if (Array.isArray(currentProgram)) {
            console.error('ERROR: currentProgram is an array! This will cause structure issues.');
            // Return a proper object structure
            return {
                modules,
                course_title: programData?.full_name || '',
                target_age_group: programAudience || '',
                description_for_educator: programData?.description || '',
                description_for_learner: programData?.description || '',
            };
        }
        
        // If currentProgram is null/undefined, create object without spreading
        if (!currentProgram) {
            return {
                modules,
                course_title: programData?.full_name || '',
                target_age_group: programAudience || '',
                description_for_educator: programData?.description || '',
                description_for_learner: programData?.description || '',
            };
        }
        
        // Normal case: currentProgram is an object, spread it and add modules
        return {
            ...currentProgram,
            modules,
            course_title: currentProgram?.course_title || programData?.full_name || '',
            target_age_group: currentProgram?.target_age_group || programAudience || '',
            description_for_educator: currentProgram?.description_for_educator || programData?.description || '',
            description_for_learner: currentProgram?.description_for_learner || programData?.description || '',
        };
    };

    const createSavePayload = (program_outline_draft) => {
        if (Array.isArray(program_outline_draft)) {
            console.error('CRITICAL ERROR: program_outline_draft is an array! Converting to object structure.');
            console.error('program_outline_draft:', program_outline_draft);
            program_outline_draft = {
                modules: program_outline_draft,
                course_title: programData?.full_name || '',
                target_age_group: programAudience || '',
                description_for_educator: programData?.description || '',
                description_for_learner: programData?.description || '',
            };
        }
        
        // Get program name and overview from edits if available
        const editedProgramName = outlineEdits['program_name']?.edited || programData?.full_name;
        const editedProgramOverview = outlineEdits['program_overview']?.edited || editedDescription || programData?.description;
        
        // Create session_updates with the entire program_layout_preview for current language (only for non-English)
        const session_updates = currentLanguage !== 'en-US' 
            ? {
                ...(programData?.session_updates || {}), // Preserve existing updates for other languages
                [currentLanguage]: {
                    program_layout_preview: program_outline_draft
                }
            }
            : programData?.session_updates || undefined;
        
        // Update translations for non-English languages
        let translations = { ...(programData?.translations || {}) };

        if (currentLanguage !== 'en-US') {
            const targetLangKey = translationLanguageKey || currentLanguage;
            const existingTranslation = translations[targetLangKey] || {};

            // Build updated translation object
            const updatedTranslation = {
                ...existingTranslation,
                // Include edited values if they exist, otherwise keep existing values
                ...(outlineEdits['program_name']?.edited ? { name: outlineEdits['program_name'].edited } : {}),
                ...(outlineEdits['program_overview']?.edited ? { description: outlineEdits['program_overview'].edited } : {}),
            };

            // Update translations object with edited name and description for current language
            translations = {
                ...translations,
                [targetLangKey]: updatedTranslation
            };
        }
        
        return {
            code: dq_module_code,
            language: 'en-US',
            full_name: currentLanguage === 'en-US' ? editedProgramName : programData?.full_name,
            target_audience: programData?.target_audience,
            dq_codes: programData?.dq_codes,
            time_to_complete_minutes: programData?.time_to_complete_minutes,
            image_url: programData?.image_url,
            theme_color: programData?.theme_color,
            badge_background: programData?.badge_background,
            primary_color: programData?.primary_color,
            description: currentLanguage === 'en-US' ? editedProgramOverview : programData?.description,
            more_info: programData?.more_info,
            translations,
            program_details: programData?.program_details,
            program_outline_draft: currentLanguage === 'en-US' ? program_outline_draft : (programData?.program_outline_draft || englishProgramLayout),
            session_updates,
        };
    };

    const handleSaveOutline = async () => {
        try {
            const editedModules = applyEditsToModules();
            const program_outline_draft = createProgramOutlineDraft(editedModules);
            const payload = createSavePayload(program_outline_draft);
            console.log('=== SAVE PAYLOAD ===');
            console.log('Current Language:', currentLanguage);
            console.log('Outline Edits:', outlineEdits);
            console.log('Translations in payload:', JSON.stringify(payload.translations, null, 2));
            console.log('Full payload keys:', Object.keys(payload));
            let res;

            // IMPORTANT:
            // Backend behavior observed:
            // - If we save non-English outline with payload language = 'en-US', preview API does NOT update that non-English preview.
            // - If we save with payload language = currentLanguage but include an English outline, it overwrites that language with English.
            //
            // So for non-English:
            // - Save translations using language='en-US'
            // - Save outline using language=currentLanguage and program_outline_draft=<translated outline>
            if (currentLanguage !== 'en-US') {
                await updateLearningProgramService(dq_module_code, {
                    code: dq_module_code,
                    language: 'en-US',
                    translations: payload.translations,
                });

                res = await updateLearningProgramService(dq_module_code, {
                    code: dq_module_code,
                    language: currentLanguage,
                    program_outline_draft,
                });
            } else {
                res = await updateLearningProgramService(dq_module_code, payload);
            }

            if (res?.data) {
                // Clear edits after successful save
                setOutlineEdits({});

                // Optimistic update so UI never shows stale preview results
                setModules(mapModulesToDisplay(editedModules));
                setCurrentProgram(program_outline_draft);

                // Refresh program metadata/english layout only; don't let stale preview overwrite outline
                await refetchProgramData({ skipOutlineUpdate: true });
            }
        } catch (err) {
            console.error('Save outline failed:', err);
            alert('Failed to save outline');
        }
    };


    // Helper functions to check if item is the last one
    const isLastModule = () => {
        return modules.length === 1;
    };

    const isLastUnit = (moduleCode) => {
        const module = modules.find(m => m.module_code === moduleCode);
        return module && module.units.length === 1;
    };

    const isLastSession = (moduleCode, unitCode) => {
        const module = modules.find(m => m.module_code === moduleCode);
        if (!module) return false;
        const unit = module.units.find(u => u.unit_code === unitCode);
        return unit && unit.lessons.length === 1;
    };

    const checkIfLastItem = (type, m_id, u_id, s_id) => {
        if (type === 'module' && isLastModule()) {
            return true;
        }
        if (type === 'unit' && isLastUnit(m_id)) {
            return true;
        }
        if (type === 'session' && isLastSession(m_id, u_id)) {
            return true;
        }
        return false;
    };

    const handleModuleCodeClick = async (module, dqco) => {
        // Use the freshly-fetched list directly — setDqcoList is async, so reading
        // the dqcoList state right after the fetch is still empty on the first click
        // (that's why it only worked on the second click).
        let list = dqcoList;
        if (list.length === 0 && initialDqcoList && initialDqcoList.length > 0) {
            list = initialDqcoList;
            setDqcoList(initialDqcoList);
        }
        if (list.length === 0) {
            // Show a spinner on THIS chip while the list loads (first click only).
            setDqcoLoadingCode(dqco);
            try {
                list = await fetchV3Level7DQCodeList();
            } finally {
                setDqcoLoadingCode(null);
            }
        }

        const dqcoObj = list.find(item => item.dqco_no === dqco);

        setPopupContent({
            code: dqco,
            dqc_no: dqcoObj?.dqc_no,
            title: dqcoObj?.full_name,
            description: dqcoObj?.definition
        });
    };

    const handleModuleFrameworkAlignedClick = (module) => {
        // Ensure we always show the default framework at the top of the list
        const DEFAULT_FRAMEWORK_NAME = 'IEEE DQ Global Standards (IEEE3527.1)';
        const defaultEntry = {
            FrameName: DEFAULT_FRAMEWORK_NAME,
            "SourceLink (same as ProgL2)": 'https://www.dqinstitute.org/global-standards/'
        };

        // Show only the frameworks actually selected/confirmed for this program at
        // build time (from params / chat wizard state) — not the full candidate
        // list that was offered during curation.
        const filtered = selectedFrameworksList.filter(f => (f?.FrameName || '').trim() !== DEFAULT_FRAMEWORK_NAME);
        const sorted = filtered.sort((a, b) => {
            const aName = String(a?.FrameName ?? '').toLowerCase();
            const bName = String(b?.FrameName ?? '').toLowerCase();
            return aName.localeCompare(bName);
        });

        setFrameAlignedContent([defaultEntry, ...sorted]);
    };

    // EDIT MODE HANDLERS
    const handleEditButtonClick = () => {
        if (isEditMode) {
            if (hasUnsavedOutlineEdits) {
                setOpenExitEditConfirmDialog(true);
            } else {
                setShowEnglishContent(false);
                setIsEditMode(false);
            }
        } else {
            // Level 22 users don't get to edit a program preview.
            if (isLevel22) return;
            // Only allow editing for non-English languages
            if (!isEditableLanguage) {
                toastWarning(tUi('outline.toast.editNotAvailableEnglish', uiLocale));
                return;
            }
            setIsEditMode(true);
        }
    };

    const handleExitEditSave = async () => {
        try {
            const editedModules = applyEditsToModules();
            const program_outline_draft = createProgramOutlineDraft(editedModules);
            const payload = createSavePayload(program_outline_draft);
            let res;
            if (currentLanguage !== 'en-US') {
                await updateLearningProgramService(dq_module_code, {
                    code: dq_module_code,
                    language: 'en-US',
                    translations: payload.translations,
                });

                res = await updateLearningProgramService(dq_module_code, {
                    code: dq_module_code,
                    language: currentLanguage,
                    program_outline_draft,
                });
            } else {
                res = await updateLearningProgramService(dq_module_code, payload);
            }
            if (res?.data) {
                setOutlineEdits({});
                // Optimistic update so UI never shows stale preview results
                setModules(mapModulesToDisplay(editedModules));
                setCurrentProgram(program_outline_draft);

                // Refresh program metadata/english layout only; don't let stale preview overwrite outline
                await refetchProgramData({ skipOutlineUpdate: true });
                setOpenExitEditConfirmDialog(false);
                setShowEnglishContent(false);
                setIsEditMode(false);
                if (pendingNavigateTo) {
                    history.push(pendingNavigateTo);
                    setPendingNavigateTo(null);
                }
            }
        } catch (err) {
            console.error('Save outline failed:', err);
            toastWarning(tUi('outline.toast.failedSaveOutline', uiLocale));
        }
    };

    const handleExitEditDiscard = () => {
        setOutlineEdits({});
        setOpenExitEditConfirmDialog(false);
        setShowEnglishContent(false);
        setIsEditMode(false);
        if (pendingNavigateTo) {
            history.push(pendingNavigateTo);
            setPendingNavigateTo(null);
        }
    };

    const handleCloseExitEditDialog = () => {
        setOpenExitEditConfirmDialog(false);
        setPendingNavigateTo(null);
    };

    const handleSaveEdit = (itemKey, fieldId, edit, sloCode = null) => {
        // For learning objectives, construct key with slo_ prefix
        const key = sloCode 
            ? `${itemKey}_slo_${sloCode}_${fieldId}`
            : `${itemKey}_${fieldId}`;
        let englishContent = '';
        const isEducator = fieldId.includes('educator');
        
        // Handle program-level fields
        if (itemKey === 'program') {
            if (fieldId === 'name') {
                englishContent = programData?.full_name || '';
            } else if (fieldId === 'overview') {
                englishContent = programData?.description || '';
            }
        } else if (sloCode) {
            // Handle learning objectives (SLOs)
            for (const mod of englishModules) {
                for (const unit of mod.units || []) {
                    const session = unit.lessons?.find(l => l.session_code === itemKey);
                    if (session && session.session_learning_objectives) {
                        const slo = session.session_learning_objectives.find(s => s.slo_code === sloCode);
                        if (slo) {
                            englishContent = slo[fieldId] || '';
                            break;
                        }
                    }
                    if (englishContent) break;
                }
                if (englishContent) break;
            }
        } else {
            // Handle module fields
            const englishModule = englishModules.find(m => m.module_code === itemKey);
            if (englishModule) {
                if (fieldId === 'module_name') {
                    englishContent = englishModule.module_name || '';
                } else if (isEducator) {
                    englishContent = englishModule.module_educator_description || '';
                } else {
                    englishContent = englishModule.module_learner_description || '';
                }
            } else {
                // Check if it's a unit
                for (const mod of englishModules) {
                    const unit = mod.units?.find(u => u.unit_code === itemKey);
                    if (unit) {
                        if (fieldId === 'unit_title') {
                            englishContent = unit.unit_title || '';
                        } else if (isEducator) {
                            englishContent = unit.unit_educator_description || '';
                        } else {
                            englishContent = unit.unit_learner_description || '';
                        }
                        break;
                    }
                }
                if (!englishContent) {
                    // Check if it's a session
                    for (const mod of englishModules) {
                        for (const unit of mod.units || []) {
                            const session = unit.lessons?.find(l => l.session_code === itemKey);
                            if (session) {
                                if (fieldId === 'lesson_title') {
                                    englishContent = session.lesson_title || '';
                                } else if (isEducator) {
                                    englishContent = session.lesson_educator_description || '';
                                } else {
                                    englishContent = session.lesson_learner_description || '';
                                }
                                break;
                            }
                        }
                        if (englishContent) break;
                    }
                }
            }
        }
        
        const editObj = typeof edit === 'string' 
            ? { original: englishContent || '', edited: edit, language: currentEditLanguage }
            : { ...edit, original: edit.original || englishContent || '' };
        setOutlineEdits(prev => ({
            ...prev,
            [key]: editObj
        }));
    };

    const handleResetEdit = (itemKey, fieldId, sloCode = null) => {
        // For learning objectives, construct key with slo_ prefix
        const key = sloCode 
            ? `${itemKey}_slo_${sloCode}_${fieldId}`
            : `${itemKey}_${fieldId}`;
        setOutlineEdits(prev => {
            const newEdits = { ...prev };
            delete newEdits[key];
            return newEdits;
        });
    };

    const displayModules = showEnglishContent ? englishModules : modules;

    const getModuleTokens = (moduleCode) => {
        const src = modules.find(m => m.module_code === moduleCode);
        return src?.unique_dqco || [];
    };

    const getSessionTokens = (sessionCode) => {
        for (const mod of modules) {
            for (const unit of mod.units || []) {
                const sess = unit.lessons?.find(l => l.session_code === sessionCode);
                if (sess) return sess.session_unique_ksab || [];
            }
        }
        return [];
    };

    const getDisplayContent = (itemKey, fieldId, originalContent) => {
        if (showEnglishContent) {
            return originalContent;
        }
        const key = `${itemKey}_${fieldId}`;
        const edit = outlineEdits[key];
        if (!edit) return originalContent;
        return edit.edited || originalContent;
    };


    const handleRemoveItem = (type, m_id, u_id, s_id) => {
        console.log("Remove item triggered:", { type, m_id, u_id, s_id });

        // Check what will happen when removing this item with full cascading logic
        let willDeleteParent = null;

        if (type === 'module' && isLastModule()) {
            willDeleteParent = 'program';
        } else if (type === 'unit') {
            // Check if removing this unit will delete the module, and if that module is the last one
            if (isLastUnit(m_id)) {
                if (isLastModule()) {
                    willDeleteParent = 'program';
                } else {
                    willDeleteParent = 'module';
                }
            }
        } else if (type === 'session') {
            // Check cascading effects: session -> unit -> module -> program
            if (isLastSession(m_id, u_id)) {
                if (isLastUnit(m_id)) {
                    if (isLastModule()) {
                        willDeleteParent = 'program';
                    } else {
                        willDeleteParent = 'module';
                    }
                } else {
                    willDeleteParent = 'unit';
                }
            }
        }

        if (willDeleteParent) {
            setLastItemToRemove({ type, m_id, u_id, s_id });
            setWillDeleteParent(willDeleteParent);
            setOpenLastItemDialog(true);
        } else {
            setItemToRemove({ type, m_id, u_id, s_id });
            setOpenDialog(true);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setItemToRemove({ type: null, m_id: null, u_id: null, s_id: null });
    };

    const handleConfirmRemove = async () => {
        const { type, m_id, u_id, s_id } = itemToRemove;

        try {
            setLoading(true);

            if (type === 'module') {
                const response = await removeModuleFromProgramService(dq_module_code, m_id);
                if (response && response.data) {
                    // Refetch all program data to get updated information
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove module');
                    setLoading(false);
                    handleCloseDialog();
                    return;
                }
            }
            else if (type === 'unit') {
                const response = await removeUnitFromModuleService(dq_module_code, m_id, u_id);
                if (response && response.data) {
                    // Refetch all program data to get updated information
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove unit');
                    setLoading(false);
                    handleCloseDialog();
                    return;
                }
            }
            else if (type === 'session') {
                const response = await removeSessionFromUnitService(dq_module_code, m_id, u_id, s_id);
                if (response && response.data) {
                    // Refetch all program data to get updated information
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove session');
                    setLoading(false);
                    handleCloseDialog();
                    return;
                }
            }

            // Reset UI states after successful removal
            // setExpandedModules([]);
            // setExpandedUnits([]);
            // setExpandedSessions({});

        } catch (error) {
            console.error(`Error removing ${type}:`, error);
            toastWarning(`Failed to remove ${type}`);
        } finally {
            setLoading(false);
            handleCloseDialog();
        }
    };

    const handleGenerateProgram = async () => {
        const request_type = REQUEST_TYPE.NEW;
        try {
            setLoading(true);
            const response = await generateLearningProgramContentService(dq_module_code, request_type);
            if (response && response.data?.initiated === true) {
                history.push(getProgramPreviewPath(dq_module_code));
            } else {
                toastWarning(response?.error_message || 'Failed to generate program content');
            }
        } catch (error) {
            console.error('Error generating program:', error);
            toastWarning('Failed to generate program content');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateClick = () => {
        // Check if it's the first free program - skip dialog if true
        const isFirstFree = programData?.is_first_free || false;
        
        if (isFirstFree) {
            // Skip confirmation for first free program
            handleGenerateProgram();
        } else {
            // Show confirmation dialog
            setOpenGenerateConfirmDialog(true);
        }
    };

    const calculateTotalSessions = () => {
        return modules.reduce((total, module) => {
            return total + (module.units || []).reduce((unitTotal, unit) => {
                return unitTotal + (unit.lessons || []).length;
            }, 0);
        }, 0);
    };

    const handleStartOver = async () => {
        console.log("Starting program", programData);
        try {
            if(programData.code) {
                const response = await clearChatStateInsiderService(programData.code);
                console.log('Clear chat state response:', response);
                if (response.data.success) {
                    history.push('/dq_pro/program/creation/preview');
                }
            }
        } catch (e) {
            console.warn('Failed to clear server chat state:', e);
        }
    };

    const handleCloseLastItemDialog = () => {
        setOpenLastItemDialog(false);
        setLastItemToRemove({ type: null, m_id: null, u_id: null, s_id: null });
        setWillDeleteParent(null);
    };

    const handleConfirmLastItemRemoval = async () => {
        const { type, m_id, u_id, s_id } = lastItemToRemove;

        try {
            setLoading(true);

            if (willDeleteParent === 'program') {
                // If removing last module, delete entire program
                await handleStartOver();
                return;
            } else if (willDeleteParent === 'module') {
                // If removing last unit from module, delete the module
                const response = await removeModuleFromProgramService(dq_module_code, m_id);
                if (response && response.data) {
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove module');
                }
            } else if (willDeleteParent === 'unit') {
                // If removing last session from unit, delete the unit
                const response = await removeUnitFromModuleService(dq_module_code, m_id, u_id);
                if (response && response.data) {
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove unit');
                }
            }

            // Reset UI states after successful removal
            // setExpandedModules([]);
            // setExpandedUnits([]);
            // setExpandedSessions({});

        } catch (error) {
            console.error(`Error handling last item removal:`, error);
            toastWarning(tUi('outline.toast.failedRemoval', uiLocale));
        } finally {
            setLoading(false);
            handleCloseLastItemDialog();
        }
    };

    const handleCloseNewProgramDialog = () => {
        setOpenNewProgramDialog(false);
        setLastItemToRemove({ type: null, m_id: null, u_id: null, s_id: null });
    };

    const getAudienceText = () => {
        const group_part = getTargetGroupLabel(programData?.target_audience) || programData?.target_audience;
        // kept for future use; currently UI only shows the group part
        // const age_part = getTargetAgeLabel(programData?.target_age);
        return `${group_part}`;
    };

    const { totalModules, totalUnits, totalSessions } = modules.reduce((acc, module) => {
        acc.totalModules += 1;
        acc.totalUnits += module.units.length;
        acc.totalSessions += module.units.reduce((unitTotal, unit) => {
            return unitTotal + (unit.lessons ? unit.lessons.length : 0);
        }, 0);
        return acc;
    }, { totalModules: 0, totalUnits: 0, totalSessions: 0 });


    const CustomToggle = ({ isEducatorView, setIsEducatorView }) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    border: '2px solid #FF6900',
                    width: {xs:'116px', sm:'155px'},
                    height: '38px',
                    backgroundColor: '#FF6900',
                    marginLeft:'auto',
                    cursor: 'pointer',
                }}
            >
                <ButtonBase
                    onClick={() => setIsEducatorView(true)}
                    sx={{
                        flex: 1,
                        backgroundColor: isEducatorView ?  '#fff' : '#FF6900',
                        color: isEducatorView ?'#FF6900' : '#fff',
                        borderRadius:'20px',
                        fontWeight: 'bold',
                        fontSize: {xs:'12px', sm:'14px'},
                        height: '100%',
                        transition: 'all 0.3s ease',
                    }}
                >
                    {tUi('outline.educator', uiLocale)}
                </ButtonBase>
                <ButtonBase
                    onClick={() => setIsEducatorView(false)}
                    sx={{
                        flex: 1,
                        backgroundColor: !isEducatorView ?  '#fff' : '#FF6900',
                        color: !isEducatorView ? '#FF6900' : '#fff',
                        fontWeight: 'bold',
                        borderRadius:'20px',
                        fontSize: {xs:'12px', sm:'14px'},
                        height: '100%',
                        transition: 'all 0.3s ease',
                    }}
                >
                    {tUi('outline.learner', uiLocale)}
                </ButtonBase>
            </Box>
        );
    };


    const handleOpenPdfInNewTab = async () => {
        if (pdfLoading) return;
        setPdfLoading(true);
        try {
            // Use HTML-based printing for perfect multi-language support
            handlePrintOutline();
        } catch (e) {
            console.error('Failed to generate PDF', e);
            toastWarning(tUi('outline.toast.failedOpenPdf', uiLocale));
        } finally {
            setPdfLoading(false);
        }
    };

    const handleBack = () => {
        const previewPath = getProgramPreviewPath(dq_module_code);
        if (isEditMode && hasUnsavedOutlineEdits) {
            setPendingNavigateTo(previewPath);
            setOpenExitEditConfirmDialog(true);
        } else {
            history.push(previewPath);
        }
    };

    // Show loader when modules/units/sessions are not loaded yet
    if (dq_module_code && modules.length === 0) {
        return (
            <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LoaderWithBackDrop loading={true} />
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ minHeight: embedded ? 'auto' : { xs: '95vh', sm: '87vh' }, mt: embedded ? 0 : {sm:'75px', lg:'auto'}, width: { xs: '100vw', sm: '100%' } }} style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: embedded ? '100%' : 'auto',
            }}>
                {!embedded && (
                    <IconButton
                        onClick={handleBack}
                        sx={{
                            position: 'absolute',
                            top: { xs: 27, md: 24 },
                            left: { xs: 16, md: 24 },
                            zIndex: 10,
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: 'var(--brand-surface, #fff)',
                            color: '#0097b2',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                            border: '1px solid rgba(0,151,178,0.15)',
                            transition: 'all 0.25s ease',
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                )}
                <div className={classes.container} style={{backgroundColor: 'transparent', padding: '0px', width: embedded ? '100%' : '90%', margin: embedded ? 'auto' : '20px auto'}} dir={isRtl ? 'rtl' : 'ltr'}>
                    {/* Edit Mode Toolbar */}
                    {isEditMode && (
                        <EditModeToolbar sx={{ 
                            width: '100%',
                            marginLeft: '-64px',
                            marginRight: '-64px',
                            padding: '18px 48px !important',
                            minHeight: '55px',
                            height: '55px',
                            gap: '24px',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                <Typography sx={{ fontSize: '17px', fontWeight: 'bold', letterSpacing: '0.3px', color: '#fff', lineHeight: 1 }}>
                                    {tUi('outline.editModeActive', uiLocale)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                {/* Language Toggle - Styled like CustomToggle */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        borderRadius: '20px',
                                        overflow: 'hidden',
                                        border: '2px solid #fff',
                                        width: '155px',
                                        height: '38px',
                                        backgroundColor: '#fff',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                    }}
                                >
                                    <ButtonBase
                                        onClick={() => setShowEnglishContent(false)}
                                        sx={{
                                            flex: 1,
                                            backgroundColor: !showEnglishContent ? '#EE6D29' : '#fff',
                                            color: !showEnglishContent ? '#fff' : '#EE6D29',
                                            borderRadius: '20px',
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            height: '100%',
                                            lineHeight: 1,
                                            whiteSpace: 'nowrap',
                                            padding: 0,
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        {currentLanguageName.split(' ')[0]}
                                    </ButtonBase>
                                    <ButtonBase
                                        onClick={() => setShowEnglishContent(true)}
                                        sx={{
                                            flex: 1,
                                            backgroundColor: showEnglishContent ? '#EE6D29' : '#fff',
                                            color: showEnglishContent ? '#fff' : '#EE6D29',
                                            fontWeight: 'bold',
                                            borderRadius: '20px',
                                            fontSize: '14px',
                                            height: '100%',
                                            lineHeight: 1,
                                            whiteSpace: 'nowrap',
                                            padding: 0,
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        en-US
                                    </ButtonBase>
                                </Box>
                                
                                {/* Save Icon Button */}
                                <Tooltip title={tUi('outline.tooltip.saveChanges', uiLocale)}>
                                    <IconButton
                                        onClick={handleSaveOutline}
                                        sx={{
                                            color: '#ffffff',
                                            width: '44px',
                                            height: '44px',
                                            borderRadius: '50%',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <SaveIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </EditModeToolbar>
                    )}
                <Box
                    className={classes.titleRow}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="91%"
                    mb={5}
                    pt={3}
                    sx={{ width: { xs: '100%', sm: '91%' } }}
                >
                    {!embedded && (isEditingProgramName ? (
                        <Box className={classes.titleBlock} sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: { xs: '100%', sm: '70%' }, maxWidth: '600px' }}>
                            <TextField
                                value={programData?.full_name || ''}
                                onChange={(e) => setProgramData(prev => ({ ...prev, full_name: e.target.value }))}
                                variant="outlined"
                                size="small"
                                placeholder={tUi('outline.placeholder.enterProgramName', uiLocale)}
                                sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                        fontWeight: '500 !important',
                                        fontSize: '1.3rem',
                                        '& input': {
                                            color: '#FF6900',
                                            fontWeight: '700 !important',
                                            caretColor: '#FF6900',
                                        },
                                        '& fieldset': {
                                            borderColor: '#FF6900',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#FF6900',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#FF6900',
                                        },
                                    }
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveProgramName}
                                    disabled={loading || !programData?.full_name?.trim()}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#ED6628',
                                        '&:hover': { backgroundColor: '#ED6628' }
                                    }}
                                >
                                    {loading ? tUi('outline.btn.saving', uiLocale) : tUi('outline.btn.save', uiLocale)}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelEditProgramName}
                                    size="small"
                                    sx={{ color: '#ED6628', borderColor: '#ED6628' }}
                                >
                                    {tUi('outline.btn.cancel', uiLocale)}
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box className={classes.titleBlock} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            {isEditMode && isEditableLanguage && !showEnglishContent ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CombinedEditableSection
                                        slide={{
                                            SlideTitle: '',
                                            SlideContent: showEnglishContent 
                                                ? (programData?.full_name || '') 
                                                : (otherLangProgramName || programData?.full_name)
                                        }}
                                        englishContent={programData?.full_name || ''}
                                        isEditMode={isEditMode}
                                        slideEdits={{
                                            'content_0': outlineEdits['program_name']
                                        }}
                                        showEnglishContent={showEnglishContent}
                                        onSave={(fieldId, edit) => handleSaveEdit('program', 'name', edit)}
                                        onReset={(fieldId) => handleResetEdit('program', 'name')}
                                        onCancel={() => {}}
                                        TitleComponent={Box}
                                        ContentComponent={Typography}
                                        contentExtraProps={{
                                            variant: 'h4',
                                            className: classes.title,
                                            sx: { mb: 0 }
                                        }}
                                    />
                                    <Typography variant="h4" className={classes.title} sx={{ mb: 0 }}>
                                        {' '}{tUi('outline.title.suffix', uiLocale)}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Typography variant="h4" className={classes.title} sx={{ mb: 0 }}>
                                        {(showEnglishContent ? (programData?.full_name || '') : (otherLangProgramName || programData?.full_name))}{' '}
                                        {tUi('outline.title.suffix', uiLocale)}
                                    </Typography>
                                    {(showEnglishContent || !isEditableLanguage) && (
                                        <Tooltip title={tUi('outline.tooltip.editProgramName', uiLocale)}>
                                            <IconButton
                                                aria-label="edit program name"
                                                size="small"
                                                style={{ color: '#ED6628' }}
                                                onClick={handleEditProgramName}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </Box>
                    ))}

                    <Box className={classes.actionsBlock} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5, flexShrink: 0, marginLeft: 'auto' }}>
                        {/* Educator/Learner toggle — always shown, including embedded
                            contexts (e.g. the program-creation preview's "View Program
                            Outline"), not just on the standalone outline page. */}
                        <CustomToggle isEducatorView={isEducatorView} setIsEducatorView={setIsEducatorView} />
                        {/* Edit outline content — inline next to the toggle instead of
                            floating over other UI. Only for non-English languages with
                            translated data, and hidden for Level 22 (partial-access) users. */}
                        {isEditableLanguage && !isLevel22 && (
                            <Tooltip
                                title={isEditMode ? tUi('outline.tooltip.exitEditMode', uiLocale) : tUi('outline.tooltip.editOutlineContent', uiLocale)}
                                arrow
                            >
                                <IconButton
                                    onClick={handleEditButtonClick}
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: isEditMode ? '#0097b2' : '#ED6628',
                                        borderRadius: '50%',
                                        width: '40px',
                                        height: '40px',
                                        padding: 0,
                                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                                        '&:hover': {
                                            backgroundColor: isEditMode ? '#0097b2' : '#d55a1f',
                                        },
                                    }}
                                >
                                    <EditIcon style={{ fontSize: '22px' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {/* Download the outline — also in the preview's embedded outline,
                            next to the toggle (LA-PW-14Oct-05), for every workspace. */}
                        <Tooltip title={tUi('outline.tooltip.download', uiLocale)}>
                            <IconButton
                                aria-label="print outline"
                                onClick={handleOpenPdfInNewTab}
                                disabled={pdfLoading || !modules.length}
                                sx={{ color: '#FF6900', borderRadius: '12px', opacity: (pdfLoading || !modules.length) ? 0.6 : 1 }}
                            >
                                {pdfLoading ? <span style={{ fontSize: 10, fontWeight: 600 }}>...</span> : <PrintIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Box>
                    {!embedded && <Box display="flex" alignItems="center" gap={1} mb={5} />}

                    <div className={classes.cardsContainer} style={embedded ? { gap: '12px' } : {}}>
                        <Card className={classes.card} style={embedded ? {
                            background: 'var(--brand-surface, #fff)',
                            border: '1px solid var(--brand-border, #e8e8e8)',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                            color: 'var(--brand-text, #1a1a1a)',
                            alignItems: 'flex-start',
                            padding: embedded && isMobile ? '10px 12px' : '16px 18px',
                            height: 'auto',
                            borderRadius: '12px',
                        } : { background: '#ffffff', border: '1px solid #e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '12px' }}>
                            {embedded && <PeopleOutline style={{ color: '#5b8dd9', fontSize: embedded && isMobile ? 18 : 22, marginBottom: 4 }} />}
                            <Typography variant="body2" style={embedded ? { color: 'var(--brand-text-muted, #888)', fontSize: embedded && isMobile ? '11px' : '13px', marginBottom: 2 } : { color: '#6A7282', fontWeight: 600, fontSize: '13px', marginBottom: 4 }}>
                                {tUi('outline.section.learners', uiLocale)}</Typography>
                            <Typography variant={embedded ? 'h6' : 'h5'} style={embedded ? { fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', lineHeight: 1.2, fontSize: embedded && isMobile ? '13px' : undefined } : { fontWeight: 800, color: '#FF6900', lineHeight: 1.2, fontSize: '20px' }}>
                                {getTargetAgeLabel(programAudience)}</Typography>
                            {embedded && <Typography variant="caption" style={{ color: 'var(--brand-text-muted, #aaa)', marginTop: 2, fontSize: embedded && isMobile ? '10px' : undefined }}>Age range</Typography>}
                        </Card>
                        <Card className={classes.card} style={embedded ? {
                            background: 'var(--brand-surface, #fff)',
                            border: '1px solid var(--brand-border, #e8e8e8)',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                            color: 'var(--brand-text, #1a1a1a)',
                            alignItems: 'flex-start',
                            padding: embedded && isMobile ? '10px 12px' : '16px 18px',
                            height: 'auto',
                            borderRadius: '12px',
                        } : { background: '#ffffff', border: '1px solid #e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '12px' }}>
                            {embedded && <MenuBook style={{ color: '#c94f7c', fontSize: embedded && isMobile ? 18 : 22, marginBottom: 4 }} />}
                            <Typography variant="body2" style={embedded ? { color: 'var(--brand-text-muted, #888)', fontSize: embedded && isMobile ? '11px' : '13px', marginBottom: 2 } : { color: '#6A7282', fontWeight: 600, fontSize: '13px', marginBottom: 4 }}>
                                {tUi('outline.section.programStructure', uiLocale)}
                            </Typography>
                            <Typography variant={embedded ? 'h6' : 'h5'} style={embedded ? { fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', lineHeight: 1.2, fontSize: embedded && isMobile ? '13px' : undefined } : { fontWeight: 800, color: '#FF6900', lineHeight: 1.2, fontSize: '20px' }}>
                                {totalModules} {totalModules === 1 ? 'Module' : 'Modules'}
                            </Typography>
                            {embedded && <Typography variant="caption" style={{ color: 'var(--brand-text-muted, #aaa)', marginTop: 2, fontSize: embedded && isMobile ? '10px' : undefined }}>{totalSessions} {totalSessions === 1 ? 'Session' : 'Sessions'}</Typography>}
                            {!embedded && <Typography variant="caption" style={{ color: '#6A7282', fontSize: '12px', marginTop: 4, fontWeight: 500 }}>
                                {tUi('outline.structure.summary', uiLocale, {
                                    modules: totalModules,
                                    units: totalUnits,
                                    sessions: totalSessions,
                                })}
                            </Typography>}
                        </Card>
                        <Card className={classes.card} style={embedded ? {
                            background: 'var(--brand-surface, #fff)',
                            border: '1px solid var(--brand-border, #e8e8e8)',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                            color: 'var(--brand-text, #1a1a1a)',
                            alignItems: 'flex-start',
                            padding: embedded && isMobile ? '10px 12px' : '16px 18px',
                            height: 'auto',
                            borderRadius: '12px',
                        } : { background: '#ffffff', border: '1px solid #e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '12px' }}>
                            {embedded && <Schedule style={{ color: '#ED6628', fontSize: embedded && isMobile ? 18 : 22, marginBottom: 4 }} />}
                            <Typography variant="body2" style={embedded ? { color: 'var(--brand-text-muted, #888)', fontSize: embedded && isMobile ? '11px' : '13px', marginBottom: 2 } : { color: '#6A7282', fontWeight: 600, fontSize: '13px', marginBottom: 4 }}>
                                {tUi('outline.section.programLength', uiLocale)}
                            </Typography>
                            <Typography variant={embedded ? 'h6' : 'h5'} style={embedded ? { fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', lineHeight: 1.2, fontSize: embedded && isMobile ? '13px' : undefined } : { fontWeight: 800, color: '#FF6900', lineHeight: 1.2, fontSize: '20px' }}>
                                {tUi('outline.duration.minutes', uiLocale, { minutes: programDuration })}
                            </Typography>
                            {embedded && <Typography variant="caption" style={{ color: 'var(--brand-text-muted, #aaa)', marginTop: 2, fontSize: embedded && isMobile ? '10px' : undefined }}>{totalSessions} {totalSessions === 1 ? 'session' : 'sessions'}</Typography>}
                        </Card>
                    </div>
                    <div className={classes.overviewContainer} style={embedded ? {
                        backgroundColor: 'var(--brand-surface, #ffffff)',
                        border: '1px solid var(--brand-border, #e8e8e8)',
                        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                        borderRadius: '12px',
                        padding: '20px',
                    } : {}}>
                        <Typography variant="h6" style={{ color: '#ED6628', marginRight: '10px', minWidth: 'fit-content', }} className={classes.overview}>
                            <b>{tUi('outline.section.overview', uiLocale)}</b>
                        </Typography>

                        {isEditingDescription ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                                <TextField
                                    multiline
                                    rows={4}
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveDescription}
                                        disabled={loading}
                                        sx={{
                                            backgroundColor: '#ED6628',
                                            '&:hover': { backgroundColor: '#d5571f' }
                                        }}
                                    >
                                        {tUi('outline.btn.save', uiLocale)}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCancelEdit}
                                        sx={{ color: '#ED6628', borderColor: '#ED6628' }}
                                    >
                                        {tUi('outline.btn.cancel', uiLocale)}
                                    </Button>
                                </Box>
                            </Box>
                        ) : isEditMode && isEditableLanguage && !showEnglishContent ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                <CombinedEditableSection
                                    slide={{
                                        SlideTitle: '',
                                        SlideContent: showEnglishContent 
                                            ? (programData?.description || '') 
                                            : (otherLangProgramDescription || programData?.description)
                                    }}
                                    englishContent={programData?.description || ''}
                                    isEditMode={isEditMode}
                                    slideEdits={{
                                        'content_0': outlineEdits['program_overview']
                                    }}
                                    showEnglishContent={showEnglishContent}
                                    onSave={(fieldId, edit) => handleSaveEdit('program', 'overview', edit)}
                                    onReset={(fieldId) => handleResetEdit('program', 'overview')}
                                    onCancel={() => {}}
                                    TitleComponent={Box}
                                    ContentComponent={Typography}
                                    contentExtraProps={{
                                        variant: 'body1',
                                        style: { marginTop: '5px', flex: 1 },
                                        className: classes.overview
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                <Typography variant="body1" style={{ marginTop: '5px', flex: 1 }}
                                            className={classes.overview}>
                                    {showEnglishContent ? (programData?.description || '') : (otherLangProgramDescription || programData?.description)}
                                </Typography>
                                {!isLevel22 && (showEnglishContent || !isEditableLanguage) && (
                                    <Tooltip title={tUi('outline.tooltip.edit', uiLocale)}>
                                        <IconButton
                                            aria-label="edit description"
                                            size="small"
                                            style={{ marginLeft: 8, color: '#ED6628' }}
                                            onClick={handleEditDescription}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </div>
                    <div className={classes.unitContainer}>
                        <Box sx={{ display: 'flex', gap: 2,marginLeft:'auto', marginRight:'auto', flexWrap: 'wrap', mb: 2 }}>
                            {Object.entries(KSAB_COLOR_MAPPING).map(([key, color]) => (
                                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            backgroundColor: color,
                                        }}
                                    />
                                    <Typography variant="body1" sx={{ color: 'var(--brand-text, #444)' }}>
                                        {getKsabLabel(key)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {displayModules.map((module, moduleIndex) => (
                            <div key={module.module_code} className={classes.unit} style={{
                                width: '100%',
                                ...(embedded ? {
                                    backgroundColor: 'var(--brand-surface, #ffffff)',
                                    border: '1px solid var(--brand-border, #e8e8e8)',
                                    borderRadius: '12px',
                                    padding: '16px 20px',
                                    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                                    marginBottom: '12px',
                                } : {}),
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    flexDirection:'column',
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}>
                                <div style={{display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <Box style={{display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between'}}>
                                    <Button
                                        onClick={() => toggleModule(moduleIndex)}
                                        disableRipple
                                        style={{
                                            color: '#EE6628',
                                            fontWeight: 'bold',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            fontSize: '18px',
                                            textAlign: 'left',
                                            justifyContent: 'space-between',
                                            display: 'flex',
                                            padding: 0,
                                            width: '100%',
                                        }}
                                        sx={{
                                            backgroundColor: 'transparent !important',
                                            '&:hover': {
                                                backgroundColor: 'transparent !important',
                                            },
                                            '&:active': {
                                                backgroundColor: 'transparent !important',
                                            },
                                            '&:focus': {
                                                backgroundColor: 'transparent !important',
                                            },
                                        }}
                                    >
                                            {isEditMode && currentLanguage !== 'en-US' && !showEnglishContent ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                    <Typography variant="h6" sx={{textTransform:'none',fontSize: {xs:'18px', sm:'1.25rem !important'}}}>
                                                        {tUi('outline.label.moduleWithIndex', uiLocale, { index: moduleIndex + 1, title: '' })}{' '}
                                                    </Typography>
                                                    <Box component="span" onClick={(e) => e.stopPropagation()}>
                                                        <CombinedEditableSection
                                                            slide={{
                                                                SlideTitle: '',
                                                                SlideContent: module.module_name
                                                            }}
                                                            englishContent={(() => {
                                                                const englishModule = englishModules.find(m => m.module_code === module.module_code);
                                                                return englishModule?.module_name || '';
                                                            })()}
                                                            isEditMode={isEditMode}
                                                            slideEdits={{
                                                                'content_0': outlineEdits[`${module.module_code}_module_name`]
                                                            }}
                                                            showEnglishContent={showEnglishContent}
                                                            onSave={(fieldId, edit) => handleSaveEdit(module.module_code, 'module_name', edit)}
                                                            onReset={(fieldId) => handleResetEdit(module.module_code, 'module_name')}
                                                            onCancel={() => {}}
                                                            TitleComponent={Box}
                                                            ContentComponent={Typography}
                                                            contentExtraProps={{
                                                                variant: 'h6',
                                                                sx: {
                                                                    textTransform: 'none',
                                                                    fontSize: {xs:'18px', sm:'1.25rem !important'},
                                                                    fontWeight: 'bold',
                                                                    color: '#EE6628'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="h6" sx={{textTransform:'none',fontSize: {xs:'18px', sm:'1.25rem !important'}}}>
                                                        {tUi('outline.duration.totalMinutes', uiLocale, { minutes: module.module_duration })}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="h6" sx={{textTransform:'none',fontSize: {xs:'18px', sm:'1.25rem !important'}}}>
                                                    {tUi('outline.label.moduleWithIndex', uiLocale, {
                                                        index: moduleIndex + 1,
                                                        title: getDisplayContent(module.module_code, 'module_name', module.module_name)
                                                    })}{' '}{tUi('outline.duration.totalMinutes', uiLocale, { minutes: module.module_duration })}
                                                </Typography>
                                            )}

                                            {expandedModules.includes(moduleIndex) ?
                                                (
                                                    <Tooltip title={tUi('outline.tooltip.collapseDetails', uiLocale)}>
                                                        <ArrowDropUp />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title={tUi('outline.tooltip.expandDetails', uiLocale)}>
                                                        <ArrowDropDown />
                                                    </Tooltip>
                                                )}
                                            </Button>

                                            {!outlineStatus.generation_initiated && (
                                              <Tooltip title={tUi('outline.tooltip.removeItem', uiLocale)}>
                                                <IconButton
                                                    onClick={() => handleRemoveItem('module', module.module_code)}
                                                    style={{ color: '#EE6628', marginLeft: '10px' }}
                                                >
                                                    <RemoveIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                        </Box>

                                        {/* Tags row */}
                                        <Box mt={1} display="flex" flexWrap="wrap" gap={1}  mb='2'>
                                            {getModuleTokens(module.module_code)?.map((dqco, idx) => {
                                                const dqcoObj = dqcoList.find(item => item.dqco_no === dqco);
                                                return (
                                                    <Typography
                                                        key={dqco || idx}
                                                        sx={{
                                                            backgroundColor: DQC_COLORS[dqcoObj?.dqc_no] || '#D2DD2C',
                                                            color: '#ffffff',
                                                            borderRadius: '20px',
                                                            fontSize: {xs:'10px', sm:'16px'},
                                                            padding: {xs:'2px 10px', sm:'2px 10px'},
                                                            fontWeight: 500,
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            if (dqcoLoadingCode) return;
                                                            handleModuleCodeClick(module, dqco);
                                                        }}
                                                    >
                                                        {dqco}
                                                        {dqcoLoadingCode === dqco && (
                                                            <CircularProgress size={10} thickness={6} sx={{ color: '#ffffff', ml: 0.75, verticalAlign: 'middle' }} />
                                                        )}
                                                    </Typography>
                                                );
                                            })}

                                            <Typography sx={{
                                                backgroundColor: '#000000',
                                                color: '#ffffff',
                                                borderRadius: '20px',
                                                fontSize: {xs:'10px', sm:'16px'},
                                                padding: {xs:'2px 10px', sm:'2px 20px'},
                                                fontWeight: 500,
                                                textAlign:'center',
                                                cursor: 'pointer',
                                            }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleModuleFrameworkAlignedClick(module);
                                            }}
                                            >
                                                {tUi('outline.frameworksAligned', uiLocale)}
                                            </Typography>
                                        </Box>

                                        {/* Framework Aligned Popup */}
                                        {frameAlignedContent && (
                                            <Box
                                                sx={{
                                                    position: 'fixed',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    width: '60%',
                                                    maxWidth: '400px',
                                                    minWidth: '300px',
                                                    maxHeight: '70vh',
                                                    backgroundColor: 'var(--brand-surface, #fff)',
                                                    border: '1px solid var(--brand-border, #000)',
                                                    borderRadius: '20px',
                                                    boxShadow: 24,
                                                    p: 3,
                                                    zIndex: 1300,
                                                    overflow: 'hidden'
                                                }}
                                                onClick={(e) => {
                                                    if (e.target === e.currentTarget) {
                                                        setFrameAlignedContent(null);
                                                    }
                                                }}
                                            >
                                                <IconButton
                                                    aria-label="close"
                                                    onClick={() => setFrameAlignedContent(null)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        color: 'var(--brand-text, #000)',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    <Close />
                                                </IconButton>

                                                {/* Fixed Header */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    mb: 2
                                                }}>
                                                    <Box sx={{
                                                        backgroundColor: '#000000',
                                                        color: 'white',
                                                        borderRadius: '20px',
                                                        px: 2,
                                                        py: 1,
                                                        fontWeight: 'medium',
                                                        fontSize: '14px'
                                                    }}>
                                                        {tUi('outline.frameworksAligned', uiLocale)}
                                                    </Box>
                                                </Box>

                                                {/* Scrollable Framework List */}
                                                <Box sx={{
                                                    width: '100%',
                                                    maxHeight: '50vh',
                                                    overflowY: 'auto',
                                                    overflowX: 'hidden',
                                                    pr: 1,
                                                    '&::-webkit-scrollbar': {
                                                        width: '6px',
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        background: '#f1f1f1',
                                                        borderRadius: '3px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        background: '#888',
                                                        borderRadius: '3px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb:hover': {
                                                        background: '#555',
                                                    }
                                                }}>
                                                    {frameAlignedContent && frameAlignedContent.length > 0 ? (
                                                        <ul style={{
                                                            listStyleType: 'disc',
                                                            paddingLeft: '20px',
                                                            margin: 0,
                                                            fontSize: '14px'
                                                        }}>
                                                            {frameAlignedContent.map((framework, index) => (
                                                                <li key={index} style={{
                                                                    marginBottom: '8px',
                                                                    color: 'var(--brand-text, #000)',
                                                                    lineHeight: '1.4'
                                                                }}>
                                                                    {framework["SourceLink (same as ProgL2)"] ? (
                                                                        <a
                                                                            href={framework["SourceLink (same as ProgL2)"]}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{
                                                                                color: 'var(--brand-text, #000)',
                                                                                textDecoration: 'underline',
                                                                                cursor: 'pointer',
                                                                                fontSize: '14px'
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            {framework.FrameName}
                                                                        </a>
                                                                    ) : (
                                                                        <span style={{
                                                                            color: 'var(--brand-text, #000)',
                                                                            fontSize: '14px'
                                                                        }}>
                                                                            {framework.FrameName}
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <Typography style={{
                                                            textAlign: 'center',
                                                            color: 'var(--brand-text-muted, #666)',
                                                            fontStyle: 'italic',
                                                            fontSize: '14px',
                                                            padding: '20px 0'
                                                        }}>
                                                            {tUi('outline.noFrameworksAligned', uiLocale)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}


                                        {popupContent && (
                                            <>
                                                <Box
                                                    onClick={() => setPopupContent(null)}
                                                    sx={{
                                                        position: 'fixed',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100vw',
                                                        height: '100vh',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                                        zIndex: 1299,
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'fixed',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '80%',
                                                        maxWidth: '500px',
                                                        backgroundColor: 'var(--brand-surface, #fff)',
                                                        border: '1px solid var(--brand-border, #000)',
                                                        borderRadius: '20px',
                                                        boxShadow: 24,
                                                        p: 4,
                                                        zIndex: 1300,
                                                    }}
                                                >
                                                    <IconButton
                                                        aria-label="close"
                                                        onClick={() => setPopupContent(null)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 0,
                                                            color: 'var(--brand-text, #000)',
                                                        }}
                                                    >
                                                        <Close />
                                                    </IconButton>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        gap: 2
                                                    }}>
                                                        <Box sx={{
                                                            backgroundColor: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C',
                                                            color: 'white',
                                                            borderRadius: '20px',
                                                            px: 2,
                                                            py: 1,
                                                            fontWeight: 'medium',
                                                            mb: 2
                                                        }}>
                                                            {popupContent.code}
                                                        </Box>

                                                        {/* Title */}
                                                        <Typography variant="h5" component="h2" style={{
                                                            fontWeight: 'bold',
                                                            textAlign: 'center',
                                                            color: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C',
                                                        }}>
                                                            {popupContent.title}
                                                        </Typography>

                                                        {/* Description */}
                                                        <Typography style={{
                                                            mt: 2,
                                                            whiteSpace: 'pre-line',
                                                            textAlign: 'left',
                                                            fontSize:'11px',
                                                            color: 'var(--brand-text, #000)'
                                                        }}>
                                                            {popupContent.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </>
                                        )}
                                    </div>
                                    <Collapse in={expandedModules.includes(moduleIndex)}>
                                        {isEditMode && currentLanguage !== 'en-US' && !showEnglishContent ? (
                                            <CombinedEditableSection
                                                slide={{
                                                    SlideTitle: '',
                                                    SlideContent: isEducatorView ? module.module_educator_description : module.module_learner_description
                                                }}
                                                isEditMode={isEditMode}
                                                slideEdits={{
                                                    'content_0': outlineEdits[`${module.module_code}_${isEducatorView ? 'educator_description' : 'learner_description'}`]
                                                }}
                                                showEnglishContent={showEnglishContent}
                                                onSave={(fieldId, edit) => handleSaveEdit(module.module_code, isEducatorView ? 'educator_description' : 'learner_description', edit)}
                                                onReset={(fieldId) => handleResetEdit(module.module_code, isEducatorView ? 'educator_description' : 'learner_description')}
                                                onCancel={() => {}}
                                                TitleComponent={Box}
                                                ContentComponent={Typography}
                                                contentExtraProps={{
                                                    variant: 'body1',
                                                    style: {
                                                        marginLeft: '8px',
                                                        padding: '8px 0',
                                                        width: '100%',
                                                        color: 'var(--brand-text-muted, #606060)'
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body1" style={{
                                                marginLeft: '8px',
                                                padding: '8px 0',
                                                width: '100%',
                                                color: 'var(--brand-text-muted, #606060)'
                                            }}>
                                                {getDisplayContent(
                                                    module.module_code,
                                                    isEducatorView ? 'educator_description' : 'learner_description',
                                                    isEducatorView ? module.module_educator_description : module.module_learner_description
                                                )}
                                            </Typography>
                                        )}
                                    </Collapse>
                                </div>

                                <Collapse in={expandedModules.includes(moduleIndex)} timeout="auto" unmountOnExit>
                                    {module.units.map((unit, unitIndex) => (
                                        <div key={unit.unit_code} className={classes.unit}
                                             style={embedded ? {
                                                 marginLeft: 0,
                                                 width: '100%',
                                                 border: '1px solid var(--brand-border, #e8e8e8)',
                                                 borderRadius: '10px',
                                                 padding: '10px 14px',
                                                 marginBottom: '8px',
                                             } : { marginLeft: '38px', width: '96%' }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                flexDirection:'column',
                                                width: '100%',
                                                justifyContent: 'space-between'
                                            }}>
                                                <div style={{display: 'flex', alignItems: 'flex-start', width: '100%', justifyContent: 'space-between'}}>
                                                <Button
                                                    onClick={() => toggleUnit(unit.unit_code)}
                                                    disableRipple
                                                    style={{
                                                        color: '#FF6900',
                                                        fontWeight: 'bold',
                                                        outline: 'none',
                                                        boxShadow: 'none',
                                                        fontSize: '18px',
                                                        justifyContent: 'space-between',
                                                        textAlign: 'left',
                                                        textTransform:'none',
                                                    }}
                                                    sx={{
                                                        backgroundColor: 'transparent !important',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent !important',
                                                        },
                                                        '&:active': {
                                                            backgroundColor: 'transparent !important',
                                                        },
                                                        '&:focus': {
                                                            backgroundColor: 'transparent !important',
                                                        },
                                                    }}
                                                    fullWidth
                                                >
                                                    {isEditMode && currentLanguage !== 'en-US' && !showEnglishContent ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                            <Typography variant="h6" sx={{textTransform:'none',fontSize: '18px'}}>
                                                                {tUi('outline.label.unitWithIndex', uiLocale, { index: unitIndex + 1, title: '' })}{' '}
                                                            </Typography>
                                                            <Box component="span" onClick={(e) => e.stopPropagation()}>
                                                                <CombinedEditableSection
                                                                    slide={{
                                                                        SlideTitle: '',
                                                                        SlideContent: unit.unit_title
                                                                    }}
                                                                    englishContent={(() => {
                                                                        const englishModule = englishModules.find(m => m.module_code === module.module_code);
                                                                        const englishUnit = englishModule?.units?.find(u => u.unit_code === unit.unit_code);
                                                                        return englishUnit?.unit_title || '';
                                                                    })()}
                                                                    isEditMode={isEditMode}
                                                                    slideEdits={{
                                                                        'content_0': outlineEdits[`${unit.unit_code}_unit_title`]
                                                                    }}
                                                                    showEnglishContent={showEnglishContent}
                                                                    onSave={(fieldId, edit) => handleSaveEdit(unit.unit_code, 'unit_title', edit)}
                                                                    onReset={(fieldId) => handleResetEdit(unit.unit_code, 'unit_title')}
                                                                    onCancel={() => {}}
                                                                    TitleComponent={Box}
                                                                    ContentComponent={Typography}
                                                                    contentExtraProps={{
                                                                        variant: 'h6',
                                                                        sx: {
                                                                            textTransform: 'none',
                                                                            fontSize: '18px',
                                                                            fontWeight: 'bold',
                                                                            color: '#FF6900'
                                                                        }
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Typography variant="h6" sx={{textTransform:'none',fontSize: '18px'}}>
                                                                {tUi('outline.duration.totalMinutes', uiLocale, { minutes: unit.unit_duration })}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="h6" sx={{textTransform:'none',fontSize: '18px'}}>
                                                            {tUi('outline.label.unitWithIndex', uiLocale, {
                                                                index: unitIndex + 1,
                                                                title: getDisplayContent(unit.unit_code, 'unit_title', unit.unit_title)
                                                            })}{' '}{tUi('outline.duration.totalMinutes', uiLocale, { minutes: unit.unit_duration })}
                                                        </Typography>
                                                    )}
                                                    {expandedUnits.includes(unit.unit_code) ?(
                                                        <Tooltip title={tUi('outline.tooltip.collapseDetails', uiLocale)}>
                                                            <ArrowDropUp />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title={tUi('outline.tooltip.expandDetails', uiLocale)}>
                                                            <ArrowDropDown />
                                                        </Tooltip>
                                                    )}
                                                </Button>
                                                {!outlineStatus.generation_initiated && (
                                                        <Tooltip title={tUi('outline.tooltip.removeItem', uiLocale)}>
                                                            <IconButton
                                                                onClick={() => handleRemoveItem('unit', module.module_code, unit.unit_code)}
                                                                style={{ color: '#EE6628', marginLeft: '10px' }}
                                                            >
                                                                <RemoveIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                )}
                                                </div>
                                                <Collapse in={expandedUnits.includes(unit.unit_code)}>
                                                    {isEditMode && currentLanguage !== 'en-US' && !showEnglishContent ? (
                                                        <CombinedEditableSection
                                                            slide={{
                                                                SlideTitle: '',
                                                                SlideContent: isEducatorView ? unit.unit_educator_description : unit.unit_learner_description
                                                            }}
                                                            isEditMode={isEditMode}
                                                            slideEdits={{
                                                                'content_0': outlineEdits[`${unit.unit_code}_${isEducatorView ? 'educator_description' : 'learner_description'}`]
                                                            }}
                                                            showEnglishContent={showEnglishContent}
                                                            onSave={(fieldId, edit) => handleSaveEdit(unit.unit_code, isEducatorView ? 'educator_description' : 'learner_description', edit)}
                                                            onReset={(fieldId) => handleResetEdit(unit.unit_code, isEducatorView ? 'educator_description' : 'learner_description')}
                                                            onCancel={() => {}}
                                                            TitleComponent={Box}
                                                            ContentComponent={Typography}
                                                            contentExtraProps={{
                                                                variant: 'body1',
                                                                style: {
                                                                    marginLeft: '8px',
                                                                    padding: '8px 0',
                                                                    width: '100%',
                                                                    color: 'var(--brand-text-muted, #606060)'
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography variant="body1" style={{
                                                            marginLeft: '8px',
                                                            padding: '8px 0',
                                                            width: '100%',
                                                            color: 'var(--brand-text-muted, #606060)'
                                                        }}>
                                                            {getDisplayContent(
                                                                unit.unit_code,
                                                                isEducatorView ? 'educator_description' : 'learner_description',
                                                                isEducatorView ? unit.unit_educator_description : unit.unit_learner_description
                                                            )}
                                                        </Typography>
                                                    )}
                                                </Collapse>
                                            </div>
                                            <Collapse in={expandedUnits.includes(unit.unit_code)} timeout="auto"
                                                      unmountOnExit>
                                                <List>
                                                    {unit.lessons && unit.lessons.map((session, sessionIndex) => (
                                                        <div key={sessionIndex}>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                width: '100%',
                                                                justifyContent: 'space-between',
                                                                padding: '8px 16px'
                                                            }}>
                                                                <Button
                                                                    onClick={() => toggleSession(session.session_code)}
                                                                    disableRipple
                                                                    style={{
                                                                        color: '#EE6628',
                                                                        fontWeight: 'bold',
                                                                        outline: 'none',
                                                                        boxShadow: 'none',
                                                                        fontSize: '18px',
                                                                        textAlign: 'left',
                                                                        justifyContent: 'space-between',
                                                                        display: 'flex',
                                                                        padding: 0,
                                                                        width: '100%',
                                                                        textTransform: 'none'
                                                                    }}
                                                                    sx={{
                                                                        backgroundColor: 'transparent !important',
                                                                        '&:hover': {
                                                                            backgroundColor: 'transparent !important',
                                                                        },
                                                                        '&:active': {
                                                                            backgroundColor: 'transparent !important',
                                                                        },
                                                                        '&:focus': {
                                                                            backgroundColor: 'transparent !important',
                                                                        },
                                                                    }}
                                                                >
                                                                    <div style={{display:'flex', flexDirection:'row', alignItems:'center'}}>
                                                                        <Typography variant="h6" className={classes.lesson}
                                                                                    style={{ marginLeft: '10px', fontSize: '18px',textTransform:'none', }}>
                                                                            {isEditMode && currentLanguage !== 'en-US' && !showEnglishContent ? (
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                    <Typography variant="h6" sx={{textTransform:'none',fontSize: '18px'}}>
                                                                                        {tUi('outline.label.sessionWithIndex', uiLocale, { index: sessionIndex + 1, title: '' })}{' '}
                                                                                    </Typography>
                                                                                    <Box component="span" onClick={(e) => e.stopPropagation()}>
                                                                                        <CombinedEditableSection
                                                                                            slide={{
                                                                                                SlideTitle: '',
                                                                                                SlideContent: session.lesson_title
                                                                                            }}
                                                                                            englishContent={(() => {
                                                                                                const englishModule = englishModules.find(m => m.module_code === module.module_code);
                                                                                                const englishUnit = englishModule?.units?.find(u => u.unit_code === unit.unit_code);
                                                                                                const englishSession = englishUnit?.lessons?.find(l => l.session_code === session.session_code);
                                                                                                return englishSession?.lesson_title || '';
                                                                                            })()}
                                                                                            isEditMode={isEditMode}
                                                                                            slideEdits={{
                                                                                                'content_0': outlineEdits[`${session.session_code}_lesson_title`]
                                                                                            }}
                                                                                            showEnglishContent={showEnglishContent}
                                                                                            onSave={(fieldId, edit) => handleSaveEdit(session.session_code, 'lesson_title', edit)}
                                                                                            onReset={(fieldId) => handleResetEdit(session.session_code, 'lesson_title')}
                                                                                            onCancel={() => {}}
                                                                                            TitleComponent={Box}
                                                                                            ContentComponent={Typography}
                                                                                            contentExtraProps={{
                                                                                                variant: 'h6',
                                                                                                sx: {
                                                                                                    textTransform: 'none',
                                                                                                    fontSize: '18px',
                                                                                                    fontWeight: 'bold'
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                    <Typography variant="h6" sx={{textTransform:'none',fontSize: '18px'}}>
                                                                                        ({tUi('outline.duration.minutes', uiLocale, { minutes: session.lesson_duration })})
                                                                                    </Typography>
                                                                                </Box>
                                                                            ) : (
                                                                                <Typography variant="h6" sx={{textTransform:'none',fontSize: '18px'}}>
                                                                                    {tUi('outline.label.sessionWithIndex', uiLocale, {
                                                                                        index: sessionIndex + 1,
                                                                                        title: getDisplayContent(session.session_code, 'lesson_title', session.lesson_title)
                                                                                    })}{' '}({tUi('outline.duration.minutes', uiLocale, { minutes: session.lesson_duration })})
                                                                                </Typography>
                                                                            )}
                                                                        </Typography>

                                                                        {/* Session Tokens row */}
                                                                        <div style={{display: 'flex',alignItems:'center', gap: '6px', marginLeft: '12px'}}>
                                                                            <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                                                                                {getSessionTokens(session.session_code)?.map((token, i) => (
                                                                                    <Box
                                                                                        key={i}
                                                                                        title={getKsabLabel(token)}
                                                                                        sx={{
                                                                                            width: 18,
                                                                                            height: 18,
                                                                                            borderRadius: '50%',
                                                                                            backgroundColor: KSAB_COLOR_MAPPING[token] || '#CCCCCC',
                                                                                        }}
                                                                                    />
                                                                                ))}
                                                                            </Box>
                                                                        </div>
                                                                    </div>

                                                                    {expandedSessions[session.session_code] ? (
                                                                        <Tooltip title={tUi('outline.tooltip.collapseDetails', uiLocale)}>
                                                                            <ArrowDropUp style={{ color: '#EE6628' }} />
                                                                        </Tooltip>
                                                                    ) : (
                                                                          <Tooltip title={tUi('outline.tooltip.expandDetails', uiLocale)}>
                                                                              <ArrowDropDown style={{ color: '#EE6628' }} />
                                                                          </Tooltip>
                                                                    )}
                                                                </Button>

                                                                {!outlineStatus.generation_initiated && (
                                                                        <Tooltip title={tUi('outline.tooltip.removeItem', uiLocale)}>
                                                                            <IconButton
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleRemoveItem('session', module.module_code, unit.unit_code, session.session_code);
                                                                                }}
                                                                                style={{ color: '#EE6628', marginLeft: '10px' }}
                                                                            >
                                                                                <RemoveIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                )}
                                                            </div>

                                                            <Collapse in={expandedSessions[session.session_code]}>
                                                                {isEditMode && currentLanguage !== 'en-US' && !showEnglishContent ? (
                                                                    <CombinedEditableSection
                                                                        slide={{
                                                                            SlideTitle: '',
                                                                            SlideContent: isEducatorView ? session.lesson_educator_description : session.lesson_learner_description
                                                                        }}
                                                                        isEditMode={isEditMode}
                                                                        slideEdits={{
                                                                            'content_0': outlineEdits[`${session.session_code}_${isEducatorView ? 'educator_description' : 'learner_description'}`]
                                                                        }}
                                                                        showEnglishContent={showEnglishContent}
                                                                        onSave={(fieldId, edit) => handleSaveEdit(session.session_code, isEducatorView ? 'educator_description' : 'learner_description', edit)}
                                                                        onReset={(fieldId) => handleResetEdit(session.session_code, isEducatorView ? 'educator_description' : 'learner_description')}
                                                                        onCancel={() => {}}
                                                                        TitleComponent={Box}
                                                                        ContentComponent={Typography}
                                                                        contentExtraProps={{
                                                                            variant: 'body1',
                                                                            style: {
                                                                                marginLeft: '8px',
                                                                                padding: '8px 0 8px 20px',
                                                                                color: 'var(--brand-text-muted, #606060)'
                                                                            }
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Typography variant="body1" style={{
                                                                        marginLeft: '8px',
                                                                        padding: '8px 0 8px 20px',
                                                                        color: 'var(--brand-text-muted, #606060)'
                                                                    }}>
                                                                        {getDisplayContent(
                                                                            session.session_code,
                                                                            isEducatorView ? 'educator_description' : 'learner_description',
                                                                            isEducatorView ? session.lesson_educator_description : session.lesson_learner_description
                                                                        )}
                                                                    </Typography>
                                                                )}
                                                            </Collapse>

                                                            <Collapse in={expandedSessions[session.session_code]}
                                                                      timeout="auto" unmountOnExit>
                                                                {session.session_learning_objectives && (
                                                                    <List>
                                                                        <ListItem style={{ paddingTop: '0px' }}>
                                                                            <Typography variant="h6" style={{
                                                                                color: 'var(--brand-text-muted, #606060)',
                                                                                marginLeft: '27px',
                                                                                fontSize: '17px',
                                                                            }}>
                                                                                {tUi('outline.section.sessionLearningObjectives', uiLocale)}
                                                                            </Typography>
                                                                        </ListItem>
                                                                        <ul style={{
                                                                            marginLeft: '40px',
                                                                            listStyleType: 'disc'
                                                                        }}>
                                                                            {session.session_learning_objectives.map((slo, sloIndex) => {
                                                                                const sloField = isEducatorView ? 'description_for_educator' : 'description_for_learner';
                                                                                const sloContent = (isEducatorView ? slo.description_for_educator : slo.description_for_learner) || slo.description || '';

                                                                                return (
                                                                                    <li key={sloIndex} style={{
                                                                                        marginBottom: '10px',
                                                                                        color: 'var(--brand-text-muted, #606060)',
                                                                                        listStyleType: 'disc'
                                                                                    }}>
                                                                                        {isEditMode && currentLanguage !== 'en-US' && !showEnglishContent ? (
                                                                                            <CombinedEditableSection
                                                                                                slide={{
                                                                                                    SlideTitle: '',
                                                                                                    SlideContent: sloContent
                                                                                                }}
                                                                                                englishContent={(() => {
                                                                                                    const englishModule = englishModules.find(m => m.module_code === module.module_code);
                                                                                                    const englishUnit = englishModule?.units?.find(u => u.unit_code === unit.unit_code);
                                                                                                    const englishSession = englishUnit?.lessons?.find(l => l.session_code === session.session_code);
                                                                                                    const englishSlo = englishSession?.session_learning_objectives?.find(s => s.slo_code === slo.slo_code);
                                                                                                    return (isEducatorView ? englishSlo?.description_for_educator : englishSlo?.description_for_learner) || englishSlo?.description || '';
                                                                                                })()}
                                                                                                isEditMode={isEditMode}
                                                                                                slideEdits={{
                                                                                                    'content_0': outlineEdits[`${session.session_code}_slo_${slo.slo_code}_${sloField}`]
                                                                                                }}
                                                                                                showEnglishContent={showEnglishContent}
                                                                                                onSave={(fieldId, edit) => handleSaveEdit(session.session_code, sloField, edit, slo.slo_code)}
                                                                                                onReset={(fieldId) => handleResetEdit(session.session_code, sloField, slo.slo_code)}
                                                                                                onCancel={() => {}}
                                                                                                TitleComponent={Box}
                                                                                                ContentComponent={Typography}
                                                                                                contentExtraProps={{
                                                                                                    variant: 'body1',
                                                                                                    style: {
                                                                                                        color: 'var(--brand-text-muted, #606060)',
                                                                                                        display: 'inline'
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                        ) : (
                                                                                            <span>
                                                                                                {getDisplayContent(
                                                                                                    `${session.session_code}_slo_${slo.slo_code}`,
                                                                                                    sloField,
                                                                                                    sloContent
                                                                                                )}
                                                                                            </span>
                                                                                        )}
                                                                                    </li>
                                                                                );
                                                                            })}
                                                                        </ul>
                                                                    </List>
                                                                )}
                                                            </Collapse>
                                                        </div>
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </div>
                                    ))}
                                </Collapse>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Updated Button Container */}
                {!embedded && (
                <div className={classes.buttonContainer}>
                    {!outlineStatus.generation_initiated && (
                        <Button
                            variant="contained"
                            onClick={() => setOpenStartOverDialog(true)}
                            style={{
                                backgroundColor: '#0097b2',
                                color: 'white',
                                margin: '0 10px',
                                padding: '10px 30px',
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                fontSize: '16px'
                            }}
                        >
                            {tUi('outline.btn.startOver', uiLocale)}
                        </Button>
                    )}
                    {!outlineStatus.generation_initiated && (
                        <Button
                        variant="contained"
                        onClick={() => {
                            if (outlineStatus.generation_initiated) {
                                history.push(getProgramPreviewPath(dq_module_code));
                            } else {
                                handleGenerateClick();
                            }
                        }}
                        disabled={outlineStatus.generation_initiated && false}
                        style={{
                            backgroundColor: outlineStatus.generation_initiated ? '#d3d3d3' : '#FF6900',
                            color: 'white',
                            margin: '0 10px',
                            padding: '10px 30px',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontSize: '16px'
                        }}
                    >
                            {tUi('outline.btn.generateProgram', uiLocale)}
                    </Button>
                    )}
                </div>
                )}
            </Box>
            <LoaderWithBackDrop loading={loading} />

            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm" PaperProps={{ sx:{ border:'2px solid var(--brand-border, #000)', borderRadius:'15px', backgroundColor:'var(--brand-surface, #fff)', backgroundImage:'none' } }}>
                <Box sx={{ p:4, position:'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{ position:'absolute', right:8, top:8, color:'var(--brand-text-muted, #999)' }}
                    >
                        <Close />
                    </IconButton>
                    <Typography variant="h6" sx={{ mt:2, mb:4, textAlign:'center', color:'#ff5757' }}>
                        {tUi('outline.dialog.removeItem.title', uiLocale, { type: itemToRemove.type })}
                    </Typography>
                    <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row'}, alignItems:'center', justifyContent:'center', gap:2, width:'100%', mt:2 }}>
                        <Button
                            variant="contained"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', fontWeight:'bold', backgroundColor:'#ff5757', '&:hover':{ backgroundColor:'#ff5757' } }}
                            onClick={handleConfirmRemove}
                        >
                            {tUi('outline.btn.yesRemove', uiLocale)}
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', borderColor:'#ff5757', color:'#ff5757', fontWeight:'bold' }}
                            onClick={handleCloseDialog}
                        >
                            {tUi('outline.btn.cancel', uiLocale)}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <Dialog open={openStartOverDialog} onClose={() => setOpenStartOverDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx:{ border:'2px solid var(--brand-border, #000)', borderRadius:'15px', backgroundColor:'var(--brand-surface, #fff)', backgroundImage:'none' } }}>
                <Box sx={{ p:4, position:'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenStartOverDialog(false)}
                        sx={{ position:'absolute', right:8, top:8, color:'var(--brand-text-muted, #999)' }}
                    >
                        <Close />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{ mt: 2, mb: 4, textAlign: 'center', color: '#ff5757' }}
                        dangerouslySetInnerHTML={{ __html: tUi('outline.dialog.removeProgram.html', uiLocale) }}
                    />
                    <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row'}, alignItems:'center', justifyContent:'center', gap:2, width:'100%', mt:2 }}>
                        <Button
                            variant="contained"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', fontWeight:'bold', backgroundColor:'#ff5757', '&:hover':{ backgroundColor:'#ff5757' } }}
                            onClick={() => { setOpenStartOverDialog(false); handleStartOver(); }}
                        >
                            {tUi('outline.btn.yesRemove', uiLocale)}
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', borderColor:'#ff5757', color:'#ff5757', fontWeight:'bold' }}
                            onClick={() => setOpenStartOverDialog(false)}
                        >
                            {tUi('outline.btn.cancel', uiLocale)}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Last Item Confirmation Dialog */}
            <Dialog open={openLastItemDialog} onClose={() => setOpenLastItemDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx:{ border:'2px solid var(--brand-border, #000)', borderRadius:'15px', backgroundColor:'var(--brand-surface, #fff)', backgroundImage:'none' } }}>
                <Box sx={{ p:4, position:'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenLastItemDialog(false)}
                        sx={{ position:'absolute', right:8, top:8, color:'var(--brand-text-muted, #999)' }}
                    >
                        <Close />
                    </IconButton>
                    <Typography variant="h6" sx={{ mt:2, mb:2, textAlign:'center', color:'#ff5757' }}>
                        {willDeleteParent === 'program'
                            ? tUi('outline.dialog.lastItem.title.program', uiLocale)
                            : willDeleteParent === 'module'
                                ? tUi('outline.dialog.lastItem.title.module', uiLocale)
                                : tUi('outline.dialog.lastItem.title.unit', uiLocale)
                        }
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ textAlign: 'center', mb: 4, color: 'var(--brand-text, #333)' }}
                        dangerouslySetInnerHTML={{
                            __html: willDeleteParent === 'program'
                                ? tUi('outline.dialog.lastItem.body.program.html', uiLocale, { type: lastItemToRemove.type })
                                : willDeleteParent === 'module'
                                    ? tUi('outline.dialog.lastItem.body.module.html', uiLocale, { type: lastItemToRemove.type })
                                    : tUi('outline.dialog.lastItem.body.unit.html', uiLocale, { type: lastItemToRemove.type })
                        }}
                    />
                    <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row'}, alignItems:'center', justifyContent:'center', gap:2, width:'100%', mt:2 }}>
                        <Button
                            variant="contained"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', fontWeight:'bold', backgroundColor:'#ff5757', '&:hover':{ backgroundColor:'#ff5757' } }}
                            onClick={async () => {
                                await handleConfirmLastItemRemoval();
                            }}
                        >
                            {willDeleteParent === 'program'
                                ? tUi('outline.dialog.lastItem.confirm.program', uiLocale)
                                : willDeleteParent === 'module'
                                    ? tUi('outline.dialog.lastItem.confirm.module', uiLocale)
                                    : tUi('outline.dialog.lastItem.confirm.unit', uiLocale)
                            }
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', borderColor:'#ff5757', color:'#ff5757', fontWeight:'bold' }}
                            onClick={() => setOpenLastItemDialog(false)}
                        >
                            {tUi('outline.btn.cancel', uiLocale)}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Generate Program Confirmation Dialog */}
            <Dialog 
                open={openGenerateConfirmDialog} 
                onClose={() => setOpenGenerateConfirmDialog(false)} 
                fullWidth 
                maxWidth="sm" 
                PaperProps={{ 
                    sx: { 
                        border: '1px solid var(--brand-border, #000)',
                        borderRadius: '13px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        backgroundImage: 'none',
                        textAlign: 'center'
                    } 
                }}
            >
                <Box sx={{ p: 4, position: 'relative' }}>
                    <Typography
                        variant="body1"
                        sx={{
                            mt: 2,
                            mb: 4,
                            textAlign: 'center',
                            fontSize: '16px',
                            lineHeight: 1.6
                        }}
                        dangerouslySetInnerHTML={{
                            __html: tUi('outline.dialog.generate.body.html', uiLocale, { count: calculateTotalSessions() })
                        }}
                    />
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' }, 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: 2, 
                            width: '100%', 
                            mt: 2 
                        }}
                    >
                        <Button
                            variant="outlined"
                            sx={{ 
                                width: { xs: '100%', sm: '30%' },
                                fontSize: '15px !important', 
                                fontWeight: 'bold', 
                                borderColor: '#0097b2',
                                borderRadius: '10px',
                                color: '#0097b2',
                                '&:hover': {
                                    borderColor: '#0097b2',
                                    backgroundColor: 'rgba(0, 151, 178, 0.1)'
                                }
                            }}
                            onClick={() => setOpenGenerateConfirmDialog(false)}
                        >
                            {tUi('outline.btn.goBack', uiLocale)}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ 
                                width: { xs: '100%', sm: '30%' },
                                fontSize: '15px !important', 
                                fontWeight: 'bold', 
                                backgroundColor: '#FF6900',
                                borderRadius: '10px',
                                '&:hover': { 
                                    backgroundColor: '#c9541e' 
                                } 
                            }}
                            onClick={() => {
                                setOpenGenerateConfirmDialog(false);
                                handleGenerateProgram();
                            }}
                        >
                            {tUi('outline.btn.yesGenerate', uiLocale)}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Exit edit mode / leave page – unsaved changes */}
            <Dialog open={openExitEditConfirmDialog} onClose={handleCloseExitEditDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { border: '2px solid var(--brand-border, #000)', borderRadius: '15px', backgroundColor: 'var(--brand-surface, #fff)', backgroundImage: 'none' } }}>
                <Box sx={{ p: 4, position: 'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseExitEditDialog}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'var(--brand-text-muted, #999)' }}
                    >
                        <Close />
                    </IconButton>
                    <Typography variant="h6" sx={{ mt: 2, mb: 2, textAlign: 'center', color: '#ff5757' }}>
                        {tUi('outline.dialog.unsaved.title', uiLocale)}
                    </Typography>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 4, color: 'var(--brand-text, #333)' }}>
                        {tUi('outline.dialog.unsaved.body', uiLocale)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'center', gap: 2, width: '100%', mt: 2 }}>
                        <Button
                            variant="outlined"
                            sx={{ width: { xs: '100%', sm: '40%' }, fontSize: '15px !important', fontWeight: 'bold', borderColor: '#ff5757', color: '#ff5757', '&:hover': { borderColor: '#ff5757', backgroundColor: 'rgba(255,87,87,0.08)' } }}
                            onClick={handleExitEditDiscard}
                        >
                            {tUi('outline.btn.discard', uiLocale)}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ width: { xs: '100%', sm: '40%' }, fontSize: '15px !important', fontWeight: 'bold', backgroundColor: '#ff5757', '&:hover': { backgroundColor: '#ff5757' } }}
                            onClick={handleExitEditSave}
                        >
                            {tUi('outline.btn.saveChanges', uiLocale)}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Hidden HTML component for printing - Supports ALL languages! */}
            <div style={{ display: 'none' }}>
                <OutlineHtmlDoc
                    ref={outlineHtmlRef}
                    programData={{
                        ...programData,
                        full_name: showEnglishContent ? programData?.full_name : (translatedName || programData?.full_name),
                        description: showEnglishContent ? programData?.description : (translatedDescription || programData?.description)
                    }}
                    programAudience={programAudience}
                    programDuration={programDuration}
                    modules={displayModules}
                    isEducatorView={isEducatorView}
                    dqcoList={dqcoList}
                    uiLocale={uiLocale}
                    selectedFrameworks={selectedFrameworksList}
                />
            </div>
        </>
    )
};

export default DQProProgOutline;
